//GraphPage.tsx

"use client";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IconButton from "@mui/material/IconButton";

import { fetchLists, fetchListItems, createList, addItem, removeItem, fetchListMembership } from "../utils/listHandler";
import type { UserList, ListItem, ListOpenLevel } from "../types/rlTypes";
import { useFavorites } from "../context/FavoritesContext";
import { addFavoriteBook, removeFavoriteBook } from "../utils/favoritesAndHistoryApi";

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
// import ForceGraph3D, { ForceGraphMethods } from "react-force-graph-3d";

import ForceGraph3DOriginal, { ForceGraphMethods } from "react-force-graph-3d";

import * as THREE from "three";

import { useAuth } from "../context/AuthContext";
import { NEW_API, buildPrefixSearchUrl } from "../config/api";
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { useAppTokens, useThemeMode } from '../context/ThemeModeContext';

import ForceGraph2DOriginal from "react-force-graph-2d";

import * as d3 from "d3-force"; // 追加（依存がなければ d3-force を入れる）
const ForceGraph2D: any = ForceGraph2DOriginal;
// 型を気にしないラッパー
const ForceGraph3D: any = ForceGraph3DOriginal;



// ========================
// 型定義
// ========================

type NodeKind = "book" | "author";

export interface GraphNode {
  id: string;
  type: NodeKind;
  // book のとき
  rlBookId?: number;
  title?: string;
  volume?: string | number | null;
  isbn?: string;
  pubYear?: number | string;
  publisher?: string;
  // author のとき
  tenkyoId?: string;
  name?: string;
  // 共通
  label?: string;
  raw?: any;
  isInUserShelf?: boolean;

  // force-graph が付与する座標
  x?: number;
  y?: number;
  z?: number;

  // 3D 用
  coverUrl?: string;

  renderR?: number;
  collideR?: number;
}

export interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
  type: string;
  weight?: number;
}

interface GraphResponse {
  nodes: any[];
  links: GraphLink[];
}

interface SnapshotInfo {
  totalRlBookIds: number;
  chunks: number;
  chunkSize: number;
}

interface AuthorExpandProgress {
  tenkyoId: string;
  done: number;
  total: number;
}

// ★ 本棚情報
interface UserHondana {
  GlobalHondanaID: string;
  OpenLevel?: string | null;
  UserSub: string;
  HondanaName: string;
  ShelfOrder: number;
  Description?: string | null;
  ColorCode?: string | null;
}

// ★ 本棚内の本（GetUserHondanaBooks の返り値用）
interface ShelfBook {
  GlobalBookID: string;
  UserSub: string;
  rl_book_id: number;
  GlobalHondanaID: string | null;
  BookOpenLevel?: string | null;
  PurchaseDate?: string | null;
  BookCondition?: string | null;
  RegisteredDatetime?: string | null;

  LastUpdate?: string | null;
  ISBN?: string | null;
  Responsibility?: string | null;
  ClassMark?: string | null;
  SchemeVersion?: string | null;
  Publisher_1?: string | null;
  Publisher_2?: string | null;
  Publisher_3?: string | null;
  PublishingDate?: string | null;
  Extent?: string | null;
  Size?: string | null;
  MainTitle?: string | null;
  ContentDetailTitle?: string | null;
  TitleOthers?: string | null;
  Audience?: string | null;

  // 追加カラムも一応許容
  [key: string]: any;
}

// ★ お気に入り一覧の1件（GET /favorites の返り値用）
interface FavoriteItem {
  rl_book_id: number;
  favorited_at: number;
  title?: string | null;
  author?: string | null;
  publisher?: string | null;
  pub_year?: number | null;
  isbn13?: string | null;
  cover_url?: string | null;
  volume?: string | number | null;
}

// ★ サジェスト API（GET /prefix）の型
interface SuggestBookResult {
  rl_book_id: number;
  title: string;
  volume?: string | number | null;
  reading?: string;
  publisher?: string;
  responsibility?: string;
  publishing_date?: string;
  isbn13?: string;
  score?: number;
}

// タイトルに巻数があれば付記する（例: "Death note" + "3" -> "Death note（3）"）
const titleWithVolume = (b: { title?: string | null; volume?: string | number | null }): string => {
  const title = b.title ?? "";
  const volumeStr = b.volume != null ? String(b.volume).trim() : "";
  return volumeStr ? `${title}（${volumeStr}）` : title;
};

interface SuggestAuthorResult {
  tenkyo_id: string;
  name: string;
  reading?: string;
  entity_type?: string;
  birth_death?: string;
  score?: number;
}

interface SuggestResponse {
  query: string;
  books: SuggestBookResult[];
  authors: SuggestAuthorResult[];
  book_next_cursor?: string | null;
}

// ========================
// 定数
// ========================

// 1回の API で投げる最大冊数
const GRAPH_BATCH_RL_BOOK_IDS = 5;

// ネットワーク全体の上限
const MAX_TOTAL_NODES = 100000;
const MAX_TOTAL_LINKS = 100000;

// 本棚からグラフで扱う最大冊数
const MAX_GRAPH_SHELF_BOOKS = 100000;

// ========================
// ユーティリティ
// ========================

const convertISBN13toISBN10 = (isbn: string): string => {
    if (!isbn || isbn.length !== 13 || !isbn.startsWith("978")) {
      throw new Error('提供されたISBNはISBN-13形式（978で始まる）ではありません。');
    }
    let isbn9 = isbn.substring(3, 12);
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += (10 - i) * parseInt(isbn9.charAt(i), 10);
    }
    let checkDigit: number | string = 11 - (sum % 11);
    checkDigit = checkDigit === 10 ? 'X' : checkDigit === 11 ? '0' : checkDigit.toString();
    return isbn9 + checkDigit;
  };

  const createAmazonLink = (isbn: string, mainTitle: string): string => {
    let amazonISBN = isbn;
    if (isbn && isbn.length === 13 && isbn.startsWith("978")) {
      try {
        amazonISBN = convertISBN13toISBN10(isbn);
      } catch (error) {
        console.error((error as Error).message);
      }
    }
    if (amazonISBN && amazonISBN.length === 10) {
      return `http://www.amazon.co.jp/dp/${amazonISBN}?&tag=readlinker202-22&linkCode=ogi`;
    } else {
      const encodedTitle = encodeURIComponent(mainTitle);
      return `https://www.amazon.co.jp/s?k=${encodedTitle}&__mk_ja_JP=%E3%82%AB%E3%82%BF%E3%82%AB%E3%83%8A`;
    }
  };

  const createCalilLink = (isbn: string) => {
    return `https://calil.jp/book/${isbn}`;
  };

  // NDL リンクは rl_book_id ベースでは生成できないので削除
  // NDL 書誌ページへのリンクが必要な場合は、ノードの raw データから ndl_bib_id を取得する


function makeLinkKey(link: GraphLink): string {
  const src =
    typeof link.source === "string" ? link.source : link.source?.id ?? "";
  const dst =
    typeof link.target === "string" ? link.target : link.target?.id ?? "";
  return `${src}-->${dst}:${link.type}`;
}

// リンクごとに安定した擬似ランダム値を作る（パーティクル表示の間引きに使用）
function hashStringToInt(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ---- 書影読み込みの同時実行数制御 ----
// グラフに数百〜数千冊が一度に表示されると、書影の並列リクエストが
// ブラウザ/CDNの処理能力を超えて一部失敗する（失敗の実体は接続断・タイムアウト
// だが、crossOrigin付きリクエストだとブラウザが「CORSポリシーでブロック」と
// 紛らわしく表示するため気づきにくい）。同時実行数を絞って順次処理する。
const MAX_CONCURRENT_COVER_LOADS = 12;
let activeCoverLoads = 0;
const coverLoadQueue: (() => void)[] = [];

function scheduleCoverLoad(run: () => void) {
  if (activeCoverLoads < MAX_CONCURRENT_COVER_LOADS) {
    activeCoverLoads++;
    run();
  } else {
    coverLoadQueue.push(run);
  }
}

function finishCoverLoad() {
  activeCoverLoads--;
  const next = coverLoadQueue.shift();
  if (next) {
    activeCoverLoads++;
    next();
  }
}

// ---- 3D 書影テクスチャ周り ----
const textureLoader = new THREE.TextureLoader();
const textureCache: Record<string, THREE.Texture> = {};

function getTexture(url: string): THREE.Texture {
  if (!textureCache[url]) {
    const tex = new THREE.Texture();
    textureCache[url] = tex;
    scheduleCoverLoad(() => {
      textureLoader.load(
        url,
        (loaded) => {
          tex.image = loaded.image;
          // 書影はsRGBで作られた画像なので明示的に指定しないと、
          // Three.jsのカラーマネジメントによりリニア色空間として扱われ、
          // 通常の<img>表示より明るく（色褪せて）見えてしまう
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.needsUpdate = true;
          finishCoverLoad();
        },
        undefined,
        () => {
          // 失敗した書影はキャッシュから外し、再選択・再描画時にリトライできるようにする
          delete textureCache[url];
          finishCoverLoad();
        }
      );
    });
  }
  return textureCache[url];
}

// 著者名などのテキスト Sprite
function createTextSprite(text: string, color: string = "white") {
  const fontSize = 20;
  const padding = 8;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  ctx.font = `bold ${fontSize}px sans-serif`;
  const textWidth = ctx.measureText(text).width;

  canvas.width = textWidth + padding * 2;
  canvas.height = fontSize + padding * 2;

  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
  });
  const sprite = new THREE.Sprite(material);

  const scale = 0.2;
  sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);

  return sprite;
}

// ========================
// API 呼び出し
// ========================

async function refreshUserSnapshot(
    idToken: string
  ): Promise<SnapshotInfo> {
    if (!idToken) {
      throw new Error("idToken is required");
    }

    // 全蔵書 rl_book_id を一括取得（POST /books の list_rl_book_ids operation）
    const res = await fetch(NEW_API.BOOKS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        operation_type: "list_rl_book_ids",
      }),
    });

    if (!res.ok) {
      throw new Error(`list_rl_book_ids failed: ${res.status}`);
    }

    const json = await res.json();
    const ids: number[] = json.data?.rl_book_ids ?? [];
    _snapshotIdsCache = ids;

    return {
      totalRlBookIds: ids.length,
      chunks: ids.length > 0 ? 1 : 0,
      chunkSize: ids.length,
    };
  }

// list_rl_book_ids の結果をチャンク互換 API 用にキャッシュ
let _snapshotIdsCache: number[] = [];

// ユーザーの本棚一覧取得（GET /shelves）
async function fetchUserHondanas(idToken: string): Promise<UserHondana[]> {
  const res = await fetch(NEW_API.SHELVES, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!res.ok) {
    throw new Error(`GET /shelves failed: ${res.status}`);
  }

  const json = await res.json();
  const shelves = (json.data?.items ?? []) as any[];

  return shelves.map((s: any): UserHondana => ({
    GlobalHondanaID: String(s.rl_global_shelf_id),
    OpenLevel: s.global_shelf_open_level ?? null,
    UserSub: String(s.user_sub ?? ""),
    HondanaName: String(s.global_shelf_name ?? ""),
    ShelfOrder:
      typeof s.global_shelf_order === "number"
        ? s.global_shelf_order
        : parseInt(s.global_shelf_order ?? "0", 10) || 0,
    Description: s.global_shelf_description ?? null,
    ColorCode: s.global_shelf_color_code ?? null,
  }));
}

// ★ 本棚内の本一覧取得（DB割り当てだけを先に取得する軽量フェーズ）
//
// 2026-08-26: 以前は GET /books?shelf_id=...（limit=100、書誌エンリッチ込み）を
// カーソルページングで逐次取得していたが、1ページごとに common/display.py経由の
// 外部API呼び出しが挟まるため、10万冊規模の本棚だと1000回近い直列リクエストが
// 必要になり、ブラウザタブが長時間ブロックされて「読込中…」のまま応答不能に
// なっていた（UserBookShelfPage.tsx で先に導入済みの minimal/enrich 分離と
// 同じ原因・同じ対処）。DB割り当てだけを取得する軽量な GET /books?minimal=true
// （1ページ最大10000件、外部API呼び出し無し）に切り替え、書誌情報は
// enrichShelfBooksDisplay で別途チャンク並列取得する
async function fetchUserHondanaBooksMinimal(
  globalHondanaId: string,
  idToken: string
): Promise<ShelfBook[]> {
  const books: ShelfBook[] = [];
  let cursor: string | null = null;

  do {
    const params = new URLSearchParams({ minimal: "true", limit: "10000" });
    if (globalHondanaId) {
      params.set("shelf_id", globalHondanaId);
    } else {
      // 疑似本棚「本棚未分類」（GlobalHondanaID === ""）は
      // shelf_id 未指定＝フィルタなし扱いになるため、明示的に no_shelf_only を送る
      params.set("no_shelf_only", "true");
    }
    if (cursor) params.set("cursor", cursor);

    const res = await fetch(`${NEW_API.BOOKS}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!res.ok) {
      throw new Error(`GET /books(minimal) failed: ${res.status}`);
    }

    const json = await res.json();
    const data = json.data ?? {};
    for (const item of (data.items ?? []) as any[]) {
      books.push({
        GlobalBookID: item.global_book_id,
        UserSub: "",
        rl_book_id: item.rl_book_id,
        GlobalHondanaID: item.rl_global_shelf_id ?? null,
        BookOpenLevel: item.global_book_open_level ?? null,
        PurchaseDate: null,
        RegisteredDatetime: item.global_book_registered_at ?? null,
        // タイトル等はこの時点では未取得。enrichShelfBooksDisplay で後から埋める
        ISBN: null,
        Responsibility: null,
        MainTitle: null,
        PublishingDate: null,
      } as ShelfBook);
    }
    cursor = data.next_cursor ?? null;
  } while (cursor);

  return books;
}

// ★ 書誌情報のエンリッチ（重い・外部API呼び出しが絡む部分）をチャンクに分割し、
// 複数レーンで並行取得しながら、チャンクが返ってくるたびに onProgress で逐次反映する。
// UserBookShelfPage.tsx の本棚読み込みと同じチャンク分割・並列レーン方式
async function enrichShelfBooksDisplay(
  books: ShelfBook[],
  idToken: string,
  onProgress: (updated: ShelfBook[]) => void
): Promise<void> {
  const indexByRlBookId = new Map<number, number[]>();
  books.forEach((b, i) => {
    const id = b.rl_book_id != null ? Number(b.rl_book_id) : null;
    if (id == null) return;
    const list = indexByRlBookId.get(id);
    if (list) list.push(i);
    else indexByRlBookId.set(id, [i]);
  });
  const uniqueRlBookIds = Array.from(indexByRlBookId.keys());
  if (uniqueRlBookIds.length === 0) return;

  const CHUNK_SIZE = 100; // バックエンド側の enrich_display 上限(200)に対して余裕を持たせる
  const LANES = 4;
  const chunks: number[][] = [];
  for (let i = 0; i < uniqueRlBookIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueRlBookIds.slice(i, i + CHUNK_SIZE));
  }

  let nextChunkIndex = 0;
  const runLane = async () => {
    while (nextChunkIndex < chunks.length) {
      const chunk = chunks[nextChunkIndex++];
      try {
        const res = await fetch(NEW_API.BOOKS, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ operation_type: "enrich_display", rl_book_ids: chunk }),
        });
        if (!res.ok) throw new Error(`enrich_display failed: ${res.status}`);
        const json = await res.json();
        const displayMap: Record<string, any> = json.data?.items ?? {};
        Object.entries(displayMap).forEach(([rlBookIdStr, disp]: [string, any]) => {
          const indices = indexByRlBookId.get(Number(rlBookIdStr));
          if (!indices) return;
          indices.forEach((idx) => {
            books[idx] = {
              ...books[idx],
              MainTitle: disp?.title ?? null,
              ISBN: disp?.isbn13 ?? null,
              Responsibility: disp?.author ?? null,
              PublishingDate: disp?.pub_year != null ? String(disp.pub_year) : null,
            };
          });
        });
        onProgress([...books]);
      } catch (chunkErr) {
        // 1チャンクの失敗で全体の読み込みは止めない（該当分だけタイトル情報が空のまま残る）
        console.error("enrich_display chunk failed", chunkErr);
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(LANES, chunks.length) }, () => runLane())
  );
}

async function fetchSnapshotChunk(chunkIndex: number, idToken: string): Promise<number[]> {
  // refreshUserSnapshot で全件取得済み（チャンクは常に1つ）
  return chunkIndex === 0 ? _snapshotIdsCache : [];
}


// API から返ってくるノードを GraphNode に正規化
function normalizeGraphNode(
  raw: any,
  userRlBookIdSet: Set<number>
): GraphNode | null {
  const id: string =
    raw.id ??
    (raw.rl_book_id != null ? String(raw.rl_book_id) : undefined) ??
    (raw.rlBookId != null ? String(raw.rlBookId) : undefined) ??
    raw.tenkyoId ??
    raw.TENKYO_ID;

  if (!id) return null;

  const nodeType: NodeKind =
    raw.type ?? (raw.rl_book_id != null || raw.rlBookId != null ? "book" : "author");

  const rlBookId: number | undefined =
    raw.rl_book_id != null ? Number(raw.rl_book_id)
    : raw.rlBookId != null ? Number(raw.rlBookId)
    : undefined;
  const tenkyoId: string | undefined = raw.tenkyoId ?? raw.TENKYO_ID;

  const title: string | undefined =
    raw.title ?? raw.MainTitle ?? raw.label ?? raw.Label;
  const volume: string | number | null | undefined = raw.volume;
  // 典拠名は NDL の「姓, 名」表記のまま来ることが多いので、UI表示用にカンマを除去する
  const rawName: string | undefined =
    raw.name ?? raw.Label ?? raw.label ?? raw.PersonName;
  const name: string | undefined =
    nodeType === "author" && rawName ? rawName.replace(/,\s*/g, " ") : rawName;

  const label =
    nodeType === "book" ? title ?? (rlBookId != null ? String(rlBookId) : id) : name ?? tenkyoId ?? id;

  const isUserBook =
    nodeType === "book" && rlBookId != null && userRlBookIdSet.has(rlBookId);

  // ★ メタデータをここで抜き出しておく（book のときだけ意味がある）
  const isbn: string | undefined = raw.isbn ?? raw.ISBN ?? raw.isbn13;
  const pubYear: number | string | undefined =
    raw.pubYear ?? raw.pub_year ?? undefined;
  const publisher: string | undefined = raw.publisher;

  // カバー URL は rl_book_id ベースで生成（他ページと同じキャッシュキーを共有するため。
  // 2026-08-26: 以前は title+isbn ベースで生成しており、同じ本でもページによって
  // 微妙に異なる title 文字列からURLを組み立てるとCloudFrontのキャッシュキーが
  // 一致せず、既に他のページで表示済みのはずの本でもキャッシュが効かなかった）
  const coverUrl =
    nodeType === "book" && (rlBookId != null || title)
      ? generateCoverUrlSmart(rlBookId, title, isbn)
      : undefined;

  return {
    id,
    type: nodeType,
    rlBookId,
    tenkyoId,
    title,
    name,
    label,
    raw,
    isInUserShelf: isUserBook,
    coverUrl,
    ...(nodeType === "book"
      ? {
          volume,
          isbn,
          pubYear,
          publisher,
        }
      : {}),
  };
}

// async function mapWithConcurrency<T, R>(
//   items: T[],
//   concurrency: number,
//   worker: (item: T, index: number) => Promise<R>
// ): Promise<R[]> {
//   const results: R[] = new Array(items.length);
//   let nextIndex = 0;

//   const runners = Array.from({ length: Math.max(1, concurrency) }, async () => {
//     while (true) {
//       const i = nextIndex++;
//       if (i >= items.length) break;
//       results[i] = await worker(items[i], i);
//     }
//   });

//   await Promise.all(runners);
//   return results;
// }

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runners = Array.from({ length: Math.max(1, concurrency) }, async () => {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) break;
      results[i] = await worker(items[i], i);
    }
  });

  await Promise.all(runners);
  return results;
}

// ★ 改良版：100冊単位 / 並列10 / 100冊ごとに onBatch で更新投げる（awaitしない）
async function fetchGraphForRlBookIdBatch(
  rlBookIds: number[],
  depth: number, // 互換のため残す（未使用）
  maxNodes: number,
  maxLinks: number,
  onBatch?: (batchRes: GraphResponse, batchIndex: number) => void | Promise<void>
): Promise<GraphResponse> {
  if (!rlBookIds || rlBookIds.length === 0) return { nodes: [], links: [] };

  const CHUNK_SIZE = 100;
  const CONCURRENCY = 10;

  const allNodes: any[] = [];
  const allLinks: GraphLink[] = [];
   // ★ onBatch を集めて最後に待つ
  const onBatchTasks: Promise<unknown>[] = [];

  for (let ci = 0; ci < Math.ceil(rlBookIds.length / CHUNK_SIZE); ci++) {
    const start = ci * CHUNK_SIZE;
    const chunk = rlBookIds.slice(start, start + CHUNK_SIZE);

    // 100冊を並列10でGetBookGraphOne
    const perBook = await mapWithConcurrency(chunk, CONCURRENCY, async (rlBookId) => {
      try {
        return await fetchGlobalBookGraphOne(rlBookId);
      } catch (e) {
        console.warn("GetBookGraphOne failed:", rlBookId, e);
        return { nodes: [], links: [] } as GraphResponse;
      }
    });

    // 100冊分を結合
    const batchNodes: any[] = [];
    const batchLinks: GraphLink[] = [];
    for (const r of perBook) {
      if (r?.nodes?.length) batchNodes.push(...r.nodes);
      if (r?.links?.length) batchLinks.push(...r.links);
    }

    // 全体にも蓄積（返り値互換用）
    if (batchNodes.length) allNodes.push(...batchNodes);
    if (batchLinks.length) allLinks.push(...batchLinks);

    if (allNodes.length > maxNodes) allNodes.length = maxNodes;
    if (allLinks.length > maxLinks) allLinks.length = maxLinks;

    // ここ重要：更新は投げるだけ（完了待ちしない）
  // 各チャンク完了時：
  if (onBatch) {
    const p = Promise.resolve(onBatch({ nodes: batchNodes, links: batchLinks }, ci)).catch(
      (e) => console.error("onBatch failed:", e)
    );
    onBatchTasks.push(p);
  }

  if (onBatchTasks.length) {
    await Promise.allSettled(onBatchTasks);
  }
  }

  return { nodes: allNodes, links: allLinks };
}


// GetGraphWithDepth(TENKYO_ID) — 新 GRAPH_ENGINE API
async function fetchAuthorGraphWithDepth(
  tenkyoId: string
): Promise<GraphResponse> {
  const res = await fetch(NEW_API.GRAPH_ENGINE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operation: "GetGraphWithDepth",
      tenkyo_id: tenkyoId,
    }),
  });

  if (!res.ok) {
    throw new Error(`GetGraphWithDepth failed: ${res.status}`);
  }

  const json = await res.json();
  const data = json.data ?? json; // {success, data:{nodes,links}} 封筒を剥がす

  return {
    nodes: (data.nodes ?? data.Nodes ?? []) as any[],
    links: (data.links ?? data.Links ?? []) as GraphLink[],
  };
}


// 本(rl_book_id)を起点に GetGraphWithDepth — 新 GRAPH_ENGINE API
async function fetchBookGraphWithDepth(
  rlBookId: number,
  depth: number
): Promise<GraphResponse> {
  const res = await fetch(NEW_API.GRAPH_ENGINE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operation: "GetGraphWithDepth",
      rl_book_id: rlBookId,
      depth,
    }),
  });

  if (!res.ok) {
    throw new Error(`GetGraphWithDepth failed: ${res.status}`);
  }

  const json = await res.json();
  const data = json.data ?? json; // {success, data:{nodes,links}} 封筒を剥がす

  return {
    nodes: (data.nodes ?? data.Nodes ?? []) as any[],
    links: (data.links ?? data.Links ?? []) as GraphLink[],
  };
}


// GetAuthorityGraphOne — 新 GRAPH_ENGINE API
async function fetchAuthorityGraphOne(tenkyoId: string): Promise<{
  nodes: any[];
  links: GraphLink[];
  bookRlBookIds: number[];
  coauthorBookRlBookIds: number[];
  soloBookRlBookIds: number[];
}> {
  const res = await fetch(NEW_API.GRAPH_ENGINE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operation: "GetAuthorityGraphOne",
      tenkyo_id: tenkyoId,
    }),
  });

  if (!res.ok) {
    throw new Error(`GetAuthorityGraphOne failed: ${res.status}`);
  }

  const json = await res.json();
  const data = json.data ?? json; // {success, data:{nodes,links}} 封筒を剥がす
  const nodes = (data.nodes ?? data.Nodes ?? []) as any[];
  const links = (data.links ?? data.Links ?? []) as GraphLink[];

  const bookRlBookIds: number[] = [];
  const coauthorBookRlBookIds: number[] = [];
  const soloBookRlBookIds: number[] = [];

  for (const node of nodes) {
    const raw = node.raw ?? node.Raw ?? {};
    const books: any[] = raw.books ?? raw.Books ?? [];
    for (const b of books) {
      const k = b.rl_book_id ?? b.rlBookId;
      if (k == null) continue;

      const rlBookId = Number(k);
      bookRlBookIds.push(rlBookId);

      let hasOtherCoauthor = false;

      for (const [key, value] of Object.entries(b)) {
        if (!key.startsWith("ContributorID_")) continue;
        if (key === "ContributorID_01") continue;

        if (value == null) continue;
        const v = String(value).trim();
        if (!v) continue;

        if (v !== tenkyoId) {
          hasOtherCoauthor = true;
          break;
        }
      }

      if (hasOtherCoauthor) {
        coauthorBookRlBookIds.push(rlBookId);
      } else {
        soloBookRlBookIds.push(rlBookId);
      }
    }
  }

  return {
    nodes,
    links,
    bookRlBookIds,
    coauthorBookRlBookIds,
    soloBookRlBookIds,
  };
}


// GetBookGraphOne — 新 GRAPH_ENGINE API
async function fetchGlobalBookGraphOne(
  rlBookId: number
): Promise<GraphResponse> {
  const res = await fetch(NEW_API.GRAPH_ENGINE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      operation: "GetBookGraphOne",
      rl_book_id: rlBookId,
    }),
  });

  if (!res.ok) {
    throw new Error(`GetBookGraphOne failed: ${res.status}`);
  }

  const json = await res.json();
  const data = json.data ?? json; // {success, data:{nodes,links}} 封筒を剥がす
  return {
    nodes: (data.nodes ?? data.Nodes ?? []) as any[],
    links: (data.links ?? data.Links ?? []) as GraphLink[],
  };
}


// ========================
// メインコンポーネント
// ========================

const GraphPage: React.FC = () => {
//   const fgRef = useRef<ForceGraphMethods | null>(null);
  const fgRef = useRef<ForceGraphMethods<any, any> | null>(null);
  const TOKENS = useAppTokens();

  // react-force-graph（2D/3D共通）はデフォルトだとコンテナのサイズを内部で
  // 自動計測するが、レイアウトが確定する前に計測してしまうことがあり、
  // 「グラフが画面の左側の一部にしか表示されず、右側の大部分が空白のまま」
  // という不具合がまれに発生していた（ResizeObserver等での再計測が働かない
  // ケースがある模様）。ここでコンテナの実サイズを自前で継続監視し、
  // width/heightを明示的に渡すことで、ライブラリ側の内部計測に頼らないようにする
  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const [graphSize, setGraphSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  useEffect(() => {
    const el = graphContainerRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      setGraphSize((prev) =>
        prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height }
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const { mode: themeMode } = useThemeMode();

  // 検索欄の配置アルゴリズム: 右上の「書影を表示」ボタン分の余白を確保しても
  // なお検索欄(+?アイコン)を画面中央に置けるだけの横幅があれば中央寄せ、
  // 無ければ従来通り左寄り（右ボタンとの重なり回避を優先）に切り替える。
  // 画面が狭いと中央寄せが左寄りに見えてしまう問題への対応（2026-08-29）。
  const SEARCH_BAR_MAX_WIDTH = 600;
  const SEARCH_BAR_RIGHT_GAP = 150; // 右余白（非中央寄せ時の右マージン、中央寄せ判定の閾値計算にも使用）
  const canCenterSearchBar = useMediaQuery(
    `(min-width:${SEARCH_BAR_MAX_WIDTH + 2 * SEARCH_BAR_RIGHT_GAP}px)`
  );

  const [snapshotInfo, setSnapshotInfo] = useState<SnapshotInfo | null>(null);
  const [userRlBookIds, setUserRlBookIds] = useState<number[]>([]);

  // ★ 本棚一覧とロード状態
  const [userHondanas, setUserHondanas] = useState<UserHondana[]>([]);
  const [loadingHondanas, setLoadingHondanas] = useState(false);

  // ★ 各本棚の中身 & ロード状態 & 展開状態
  const [shelfBooksMap, setShelfBooksMap] = useState<Record<string, ShelfBook[]>>({});
  const [loadingShelfIds, setLoadingShelfIds] = useState<string[]>([]);
  // ★ 割り当て(minimal)取得後、書誌情報のエンリッチをバックグラウンドで継続中の本棚。
  // 本一覧自体はすでに表示できているので loadingShelfIds とは別に管理する
  // （エンリッチ中はタイトル未取得の本に「取得中…」を出し分けるためだけに使う）
  const [enrichingShelfIds, setEnrichingShelfIds] = useState<string[]>([]);
  const [expandedShelfIds, setExpandedShelfIds] = useState<string[]>([]);
  // ★ 本棚タブ／リストタブのリロードアイコン用ローディング状態
  const [shelfTabReloading, setShelfTabReloading] = useState(false);
  const [listTabReloading, setListTabReloading] = useState(false);

  // ★ 本棚タブ用：複数選択 & フィルタ
  const [shelfMultiSelectMode, setShelfMultiSelectMode] = useState<boolean>(false);
  const [selectedShelfIds, setSelectedShelfIds] = useState<string[]>([]);
  const [selectedShelfBookIds, setSelectedShelfBookIds] = useState<string[]>([]);
  const [shelfFilterText, setShelfFilterText] = useState<string>("");

  const { isLoggedIn, idToken, userSub } = useAuth();

  // ★ 書誌詳細パネル：お気に入り（書籍詳細ページと同じブックマーク機能）
  const { state: favoritesState, dispatch: favoritesDispatch } = useFavorites();


  const [graphData, setGraphData] = useState<{
    nodes: GraphNode[];
    links: GraphLink[];
  }>({ nodes: [], links: [] });

  // ★ 表示中のノード数（典拠/本）
  const { visibleAuthorCount, visibleBookCount } = useMemo(() => {
    let authors = 0;
    let books = 0;

    for (const n of graphData.nodes ?? []) {
      if (n?.type === "author") authors += 1;
      else if (n?.type === "book") books += 1;
    }

    return { visibleAuthorCount: authors, visibleBookCount: books };
  }, [graphData.nodes]);

  const shelfBuildRunIdRef = useRef(0);
  const listBuildRunIdRef = useRef(0);

  // ★ グラフ上に存在する本の rl_book_id セット
  const graphBookRlBookIdSet = useMemo(() => {
    const s = new Set<number>();
    for (const n of graphData.nodes) {
      if (n.type === "book" && n.rlBookId != null) {
        s.add(n.rlBookId);
      }
    }
    return s;
  }, [graphData.nodes]);

  // グラフ構築対象として使う rl_book_id 一覧
  const [graphTargetRlBookIds, setGraphTargetRlBookIds] = useState<number[]>([]);
  const [nextBatchIndex, setNextBatchIndex] = useState(0);
  const [maxBatchCount, setMaxBatchCount] = useState(0);

  // ノード / リンクの蓄積
  const nodeMapRef = useRef<Map<string, GraphNode>>(new Map());
  const linkMapRef = useRef<Map<string, GraphLink>>(new Map());

  // 3Dモードで選択中ノードを囲む「動く点線」の描画対象（isSelectedの枠を作った時に登録される）
  const selectedBorderAnimRef = useRef<{
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
    size: number;
  } | null>(null);

  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [graphBuildProgress, setGraphBuildProgress] = useState<{
    source: "shelf" | "list";
    done: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  // マウスオーバー中のノード（どの本/典拠にカーソルが乗っているか分かるように色を変える用）
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const handleNodeHover = useCallback((node: any) => {
    setHoveredNodeId(node ? node.id : null);
  }, []);

  // ★ 書誌詳細パネル：この本の著者一覧（Amazon/カーリルの下に表示するボタン用）
  const [panelBookAuthors, setPanelBookAuthors] = useState<GraphNode[]>([]);
  const [panelBookAuthorsLoading, setPanelBookAuthorsLoading] = useState(false);

  // ========================
  // リスト追加（BookList）
  // ========================
  const [bookLists, setBookLists] = useState<UserList[]>([]);
  // 選択中の本がすでに入っているリストID（緑チェック表示・トグル判定用）
  const [inListIds, setInListIds] = useState<Set<string>>(new Set());
  const [listMenuAnchorEl, setListMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openCreateListDialog, setOpenCreateListDialog] = useState(false);
  const [newListName, setNewListName] = useState("気になる本");
  const [newListOpenLevel, setNewListOpenLevel] = useState<ListOpenLevel>("private");
  const [isCreatingList, setIsCreatingList] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ★ 未ログインの初回訪問者向けオンボーディングモーダル。
  // このページは初期状態だと空のグラフのみで、何をすればいいか分からず
  // 離脱してしまう問題があった（2026-08-29発覚）。ログイン済みユーザーは
  // 既にこの画面に慣れているはずなので対象外、localStorageのフラグで
  // 同じブラウザでは一度きりの表示にする。
  const GRAPH_ONBOARDING_SEEN_KEY = "readlinker_graph_onboarding_seen";
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  useEffect(() => {
    if (isLoggedIn) return;
    try {
      if (localStorage.getItem(GRAPH_ONBOARDING_SEEN_KEY)) return;
    } catch {
      // localStorage不可の環境ではモーダルを出さない（毎回出るよりは無難）
      return;
    }
    setShowOnboardingModal(true);
  }, [isLoggedIn]);
  const handleCloseOnboardingModal = () => {
    setShowOnboardingModal(false);
    try {
      localStorage.setItem(GRAPH_ONBOARDING_SEEN_KEY, "1");
    } catch {
      // 保存できなくても閉じる動作自体は成立させる
    }
  };

  // 選択中ノードから rl_book_id を推定
  const getSelectedRlBookId = useCallback((): number | null => {
    if (!selectedNode || selectedNode.type !== "book") return null;
    if (selectedNode.rlBookId != null) return selectedNode.rlBookId;
    const id = selectedNode.id ?? "";
    const parsed = Number(id);
    if (!isNaN(parsed) && parsed > 0) return parsed;
    return null;
  }, [selectedNode]);

  const fetchBookLists = useCallback(async () => {
    if (!idToken) return;
    try {
      const res = await fetchLists(idToken);
      const sorted = (res.items ?? []).slice().sort(
        (a, b) => (a.user_list_order ?? 0) - (b.user_list_order ?? 0),
      );
      setBookLists(sorted);
    } catch (e) {
      console.error("Error fetching book lists:", e);
    }
  }, [idToken]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchBookLists();
  }, [fetchBookLists, isLoggedIn]);

  // 選択中の本がどのリストに入っているか（緑チェック表示用）。選択が変わるたびに
  // 軽量なAPIで取得し直す（リスト・アイテム数によらず高速なため、永続キャッシュはしない）
  useEffect(() => {
    const rlBookId = getSelectedRlBookId();
    if (rlBookId == null || !idToken) {
      setInListIds(new Set());
      return;
    }
    let cancelled = false;
    fetchListMembership(rlBookId, idToken)
      .then((ids) => { if (!cancelled) setInListIds(ids); })
      .catch((e) => console.error("Error fetching list membership:", e));
    return () => { cancelled = true; };
  }, [selectedNode?.id, selectedNode?.type, selectedNode?.rlBookId, idToken, getSelectedRlBookId]);

  const handleListMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setListMenuAnchorEl(event.currentTarget);
  };

  const handleListMenuClose = () => {
    setListMenuAnchorEl(null);
  };

  const openSnackbar = (msg: string) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const handleToggleListItem = async (listId: string) => {
    const rlBookId = getSelectedRlBookId();
    if (rlBookId == null || !idToken) return;

    const wasInList = inListIds.has(listId);
    try {
      if (wasInList) {
        await removeItem(listId, rlBookId, idToken);
        setInListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
        openSnackbar("リストから外しました");
      } else {
        await addItem(listId, rlBookId, null, idToken);
        setInListIds((prev) => new Set(prev).add(listId));
        openSnackbar("リストへ追加しました");
      }
    } catch (e: any) {
      const status = e?.response?.status;
      // 409(既に追加済み)・404(既に削除済み)は、表示していたチェック状態が実際の
      // サーバー状態とズレていただけの想定内のケース。ユーザーから見れば意図通りの
      // 結果になっているだけなので、通知は出さず表示状態を静かに自己修正するだけに留める
      if (status === 409) {
        setInListIds((prev) => new Set(prev).add(listId));
      } else if (status === 404) {
        setInListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
      } else {
        console.error("Error toggling book in list:", e);
        openSnackbar("エラーが発生しました");
      }
    } finally {
      handleListMenuClose();
    }
  };

  const handleCreateNewList = () => {
    handleListMenuClose();
    setOpenCreateListDialog(true);
  };

  const handleCloseCreateListDialog = () => {
    setOpenCreateListDialog(false);
    setNewListName("気になる本");
    setNewListOpenLevel("private");
  };

  const handleCreateList = async () => {
    if (!idToken || !userSub) return;

    setIsCreatingList(true);
    try {
      await createList(newListName, null, newListOpenLevel, idToken);
      await fetchBookLists();
      handleCloseCreateListDialog();
      openSnackbar("リストを作成しました");
    } catch (e) {
      console.error("Error creating new list:", e);
      openSnackbar("エラーが発生しました");
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  type ShelfHighlightMode = 0 | 1 | 2;
  const [shelfHighlightMode, setShelfHighlightMode] = useState<ShelfHighlightMode>(0);

  // 自動ロード ON/OFF
  const [autoLoad, setAutoLoad] = useState(false);

  // ★ ID 指定追加用
  const [idInput, setIdInput] = useState("");
  const [idSearchError, setIdSearchError] = useState<string | null>(null);

  // ★ サジェスト検索用
  const [searchText, setSearchText] = useState("");
  // 検索欄にフォーカスがあるかどうか（外側クリックで候補だけ隠し、データは保持したまま）
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // 検索ヒント（?アイコン）の表示状態
  const [searchHintOpen, setSearchHintOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchBooks, setSearchBooks] = useState<SuggestBookResult[]>([]);
  const [searchAuthors, setSearchAuthors] = useState<SuggestAuthorResult[]>([]);
  // 書籍の「もっと見る」用カーソル。クエリの文字種を問わず発行される
  const [searchBookNextCursor, setSearchBookNextCursor] = useState<string | null>(null);
  const [loadingMoreSearchBooks, setLoadingMoreSearchBooks] = useState(false);
  // マウスオーバー中のサジェスト候補（"author:tenkyoId" / "book:rlBookId"）
  const [hoveredSuggestKey, setHoveredSuggestKey] = useState<string | null>(null);
  // 入力が rl_book_id / tenkyo_id に完全一致した場合のみセットする（該当なしはサイレントに無視）
  const [idMatchBook, setIdMatchBook] = useState<SuggestBookResult | null>(null);
  const [idMatchAuthor, setIdMatchAuthor] = useState<SuggestAuthorResult | null>(null);

  // 左コンソールのタブ（"browse" = 本棚/リスト/お気に入りのいずれか、"graph" = 表示中の典拠と本）
  const [leftPanelTab, setLeftPanelTab] = useState<"browse" | "graph">("graph");
  // "browse" タブ内でどのデータソースを見るか
  const [browseMode, setBrowseMode] = useState<"shelf" | "list" | "favorites">("shelf");

  // 未ログインになったら本棚/リスト/お気に入りタブは意味を持たないので「表示中の典拠と本」へ戻す
  useEffect(() => {
    if (!isLoggedIn) setLeftPanelTab("graph");
  }, [isLoggedIn]);

  // ★ リストタブ用（一覧＝bookLists を流用。中身は本棚と同じ要領で遅延ロード）
  const [listItemsMap, setListItemsMap] = useState<Record<string, ListItem[]>>({});
  const [loadingListIds, setLoadingListIds] = useState<string[]>([]);
  const [expandedListIds, setExpandedListIds] = useState<string[]>([]);
  const [listFilterText2, setListFilterText2] = useState("");
  const [fetchAllListDetailsRunning, setFetchAllListDetailsRunning] = useState(false);
  const [fetchAllListDetailsProgress, setFetchAllListDetailsProgress] = useState<{ done: number; total: number } | null>(null);
  const [allListDetailsFetched, setAllListDetailsFetched] = useState(false);

  // ★ リストタブ用：複数選択（本棚タブと同じ要領）
  // selectedListBookIdsのキーは `${user_list_id}::${rl_book_id}`（同じ本が複数リストに
  // 入っていてもリストごとに独立して選択できるようにするため）
  const [listMultiSelectMode, setListMultiSelectMode] = useState<boolean>(false);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [selectedListBookIds, setSelectedListBookIds] = useState<string[]>([]);

  // ★ お気に入りタブ用（100件ずつページネーション）
  const [favoriteItems, setFavoriteItems] = useState<FavoriteItem[]>([]);
  const [favoritesNextCursor, setFavoritesNextCursor] = useState<string | null>(null);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [favoritesLoadedOnce, setFavoritesLoadedOnce] = useState(false);

  // 左スライドパネルの折りたたみ（主にスマホ向け）
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) setLeftPanelTab("graph");
  }, [isLoggedIn]);
  // 典拠でまとめる（グラフタブ側）
  const [groupByAuthority, setGroupByAuthority] = useState(true);
  // 表示中の本タブのフィルタ
  const [listFilterText, setListFilterText] = useState("");
  // 著者の展開状態（グラフタブ）
  const [expandedAuthorRowIds, setExpandedAuthorRowIds] = useState<string[]>([]);



  // 「リンクを伸ばした本」の rl_book_id リスト
  const [expandedBookRlBookIds, setExpandedBookRlBookIds] = useState<number[]>(
    []
  );

  // 「すべての本のリンクを伸ばした著者」の TENKYO_ID リスト
  const [expandedAuthorTenkyoIds, setExpandedAuthorTenkyoIds] = useState<
    string[]
  >([]);

  // 著者一括展開の進捗
  const [authorExpandProgress, setAuthorExpandProgress] =
    useState<AuthorExpandProgress | null>(null);

  // ★ 著者のリンク展開キャンセル用
  const [authorExpandCancelFor, setAuthorExpandCancelFor] = useState<string | null>(null);
  const authorExpandCancelRef = useRef<string | null>(null);

  // カメラ回転用の角度
  const orbitAngleRef = useRef(0);

  const [showClearConfirm, setShowClearConfirm] = useState(false);

  //2d対応
  const [is2DMode, setIs2DMode] = useState(false);

  const coverImgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

    const getCoverUrl = useCallback((node: any) => {
    return (
        node.coverUrl ??
        (node.rlBookId != null || node.title
          ? generateCoverUrlSmart(node.rlBookId, node.title, node.isbn)
          : undefined)
    );
    }, []);

  const [fetchAllShelfDetailsRunning, setFetchAllShelfDetailsRunning] = useState(false);
  const [fetchAllShelfDetailsProgress, setFetchAllShelfDetailsProgress] = useState<{ done: number; total: number } | null>(null);
  const [allShelfDetailsFetched, setAllShelfDetailsFetched] = useState(false);

  
  // 1バッチ分だけグラフを拡張する関数
  const loadNextBatch = async (
    targetIdsParam?: number[],
    batchIndexParam?: number,
    userSetParam?: Set<number>
  ) => {
    const targetIds = targetIdsParam ?? graphTargetRlBookIds;
    const batchIndex = batchIndexParam ?? nextBatchIndex;
    const userSet = userSetParam ?? new Set(userRlBookIds);

    if (loadingGraph) return;
    if (targetIds.length === 0) return;

    // maxBatchCount(state) はクロージャで古い値を掴むことがあるため、
    // targetIds から都度導出する（起動直後の setMaxBatchCount 直後呼び出し対策）
    const totalBatches = Math.ceil(targetIds.length / GRAPH_BATCH_RL_BOOK_IDS);
    if (batchIndex >= totalBatches) return;

    if (
      nodeMapRef.current.size >= MAX_TOTAL_NODES ||
      linkMapRef.current.size >= MAX_TOTAL_LINKS
    ) {
      console.log("Reached max total graph size, stop loading more.");
      return;
    }

    const start = batchIndex * GRAPH_BATCH_RL_BOOK_IDS;
    const batch = targetIds.slice(start, start + GRAPH_BATCH_RL_BOOK_IDS);
    if (batch.length === 0) return;

    console.log(
      `loadNextBatch: batch ${batchIndex + 1}/${totalBatches}, ${
        batch.length
      } rl_book_ids`
    );

    setLoadingGraph(true);
    try {
      const res = await fetchGraphForRlBookIdBatch(
        batch,
        2,
        MAX_TOTAL_NODES,
        MAX_TOTAL_LINKS
      );

      for (const rawNode of res.nodes ?? []) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);

        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }
      }

      for (const link of res.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      setNextBatchIndex((prev) => prev + 1);
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "グラフロード中にエラーが発生しました"
      );
    } finally {
      setGraphBuildProgress(null);
      setLoadingGraph(false);
    }
  };

  const addShelfBooksToGraph = async (rlBookIds: number[]) => {
    const targetIds = rlBookIds.filter((id) => id != null && !graphBookRlBookIdSet.has(id));
    if (targetIds.length === 0) return;

    const userSet = new Set(userRlBookIds);

    setLoadingGraph(true);
    setError(null);

    const total = targetIds.length;

    const runId = ++shelfBuildRunIdRef.current; // ★この実行のID
    setGraphBuildProgress({ source: "shelf", done: 0, total });

    try {
      await fetchGraphForRlBookIdBatch(
        targetIds,
        1,
        MAX_TOTAL_NODES,
        MAX_TOTAL_LINKS,
        (batchRes, batchIndex) => {
          // ★別の追加が始まってたら無視
          if (shelfBuildRunIdRef.current !== runId) return;
  
          const done = Math.min((batchIndex + 1) * 100, total);
          applyBatchToGraph(batchRes, userSet);
  
          // ★最後だけは “進捗表示を消す” を優先
          if (done >= total) {
            setGraphBuildProgress(null);
          } else {
            setGraphBuildProgress({ source: "shelf", done, total });
          }
        }
      );
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? "本棚からグラフへの追加中にエラーが発生しました");
    } finally {
      // ★最後の onBatch で消してるので、ここでは消さない（消すなら runId一致条件付き）
      if (shelfBuildRunIdRef.current === runId) {
        setLoadingGraph(false);
      }
    }
  };
  


  // ★ rl_book_id を1件グラフに追加するハンドラ（本棚・リスト・お気に入り共通）
  const handleAddOneBookToGraphById = async (rlBookId: number) => {
    await addShelfBooksToGraph([rlBookId]);

    // 1冊だけの追加時は、追加した本を中心に表示する
    // （複数冊まとめて追加したときは今まで通り視点を動かさない）
    const addedNode =
      nodeMapRef.current.get(`book:${rlBookId}`) ??
      nodeMapRef.current.get(String(rlBookId));
    if (addedNode) {
      setTimeout(() => handleNodeClick(addedNode), 300);
    }
  };

  // ★ 単一本（ShelfBook）をグラフに追加するハンドラ
  const handleAddOneShelfBookToGraph = async (book: ShelfBook) => {
    if (book.rl_book_id == null) return;
    await handleAddOneBookToGraphById(Number(book.rl_book_id));
  };

  // ★ 既にグラフ上にある本を選択状態にして中心に持ってくる（本棚/リスト/お気に入りの「→」ボタン用）
  const handleFocusBookInGraph = (rlBookId: number) => {
    const node =
      nodeMapRef.current.get(`book:${rlBookId}`) ??
      nodeMapRef.current.get(String(rlBookId));
    if (node) handleNodeClick(node);
  };

  // ★ リスト内の本の行をクリック → 展開/折りたたみ（初回は本一覧を遅延ロード）
  const handleClickListRow = async (list: UserList) => {
    const id = list.user_list_id;
    const isExpanded = expandedListIds.includes(id);

    if (isExpanded) {
      setExpandedListIds((prev) => prev.filter((x) => x !== id));
      return;
    }
    setExpandedListIds((prev) => [...prev, id]);

    if (listItemsMap[id] || !idToken) return; // 既にロード済み

    setLoadingListIds((prev) => [...prev, id]);
    try {
      const res = await fetchListItems(id, idToken);
      setListItemsMap((prev) => ({ ...prev, [id]: res.items ?? [] }));
    } catch (e) {
      console.error("Error fetching list items:", e);
    } finally {
      setLoadingListIds((prev) => prev.filter((x) => x !== id));
    }
  };

  // ★ リスト単位のチェック ON/OFF（本棚のhandleToggleShelfSelectedと同じ要領）
  const handleToggleListSelected = (listId: string, checked: boolean) => {
    const items = listItemsMap[listId] ?? [];

    setSelectedListIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(listId);
      } else {
        set.delete(listId);
      }
      return Array.from(set);
    });

    if (items.length === 0) return;

    setSelectedListBookIds((prev) => {
      const set = new Set(prev);
      for (const item of items) {
        const key = `${listId}::${item.rl_book_id}`;
        if (checked) {
          set.add(key);
        } else {
          set.delete(key);
        }
      }
      return Array.from(set);
    });
  };

  // ★ 本単位のチェック ON/OFF（リスト内の1冊ずつ）
  const handleToggleListBookSelected = (
    listId: string,
    rlBookId: number,
    checked: boolean
  ) => {
    const items = listItemsMap[listId] ?? [];
    const key = `${listId}::${rlBookId}`;

    setSelectedListBookIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(key);
      } else {
        set.delete(key);
      }

      const allSelected =
        items.length > 0 &&
        items.every((item) => set.has(`${listId}::${item.rl_book_id}`));

      setSelectedListIds((prevLists) => {
        const s = new Set(prevLists);
        if (allSelected) {
          s.add(listId);
        } else {
          s.delete(listId);
        }
        return Array.from(s);
      });

      return Array.from(set);
    });
  };

  // ★ 複数選択ボタン（リストタブ）
  const handleToggleListMultiSelect = () => {
    setListMultiSelectMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedListIds([]);
        setSelectedListBookIds([]);
      }
      return next;
    });
  };

  // ★ 選択したリスト（内の本）をまとめてグラフに追加
  const handleAddSelectedListBooksToGraph = async () => {
    if (!listMultiSelectMode) return;
    if (selectedListBookIds.length === 0) return;

    try {
      setError(null);
      setLoadingGraph(true);

      const rlBookIdList: number[] = [];
      for (const key of selectedListBookIds) {
        const rlBookIdStr = key.split("::")[1];
        const rlBookId = Number(rlBookIdStr);
        if (!Number.isNaN(rlBookId)) rlBookIdList.push(rlBookId);
      }

      const uniqueRlBookIds = [...new Set(rlBookIdList)];
      if (uniqueRlBookIds.length === 0) return;

      const userSet = new Set(userRlBookIds);

      // ★ run ガード（この処理が最新か判定）
      const runId = ++listBuildRunIdRef.current;
      const total = uniqueRlBookIds.length;
      setGraphBuildProgress({ source: "list", done: 0, total });

      await fetchGraphForRlBookIdBatch(
        uniqueRlBookIds,
        1,
        MAX_TOTAL_NODES,
        MAX_TOTAL_LINKS,
        (batchRes, batchIndex) => {
          if (listBuildRunIdRef.current !== runId) return;

          const done = Math.min((batchIndex + 1) * 100, total);
          applyBatchToGraph(batchRes, userSet);

          if (done >= total) setGraphBuildProgress(null);
          else setGraphBuildProgress({ source: "list", done, total });
        }
      );
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "グラフ追加中にエラーが発生しました"
      );
    } finally {
      setLoadingGraph(false);
      if (graphBuildProgress && graphBuildProgress.done >= graphBuildProgress.total) {
        setGraphBuildProgress(null);
      }

      setSelectedListIds([]);
      setSelectedListBookIds([]);
      setListMultiSelectMode(false);
    }
  };

  // ★ 全てのリストの本を取得（本棚の「全ての本棚の本を取得」と同じ要領。
  // 既にlistItemsMapにあるリストはスキップし、無いものだけ順番に取得する）
  const handleFetchAllListDetails = async () => {
    if (!isLoggedIn || !idToken) return;
    if (fetchAllListDetailsRunning) return;
    if (bookLists.length === 0) return;

    setFetchAllListDetailsRunning(true);
    setError(null);

    const total = bookLists.length;
    setFetchAllListDetailsProgress({ done: 0, total });

    try {
      for (let i = 0; i < bookLists.length; i++) {
        const id = bookLists[i].user_list_id;

        if (!listItemsMap[id]) {
          try {
            const res = await fetchListItems(id, idToken);
            setListItemsMap((prev) => ({ ...prev, [id]: res.items ?? [] }));
          } catch (e) {
            console.error("Error fetching list items:", e);
          }
        }

        setFetchAllListDetailsProgress({ done: i + 1, total });
        await new Promise((r) => setTimeout(r, 0));
      }
      setAllListDetailsFetched(true);
    } catch (e) {
      console.error(e);
      setError((prev) => prev ?? "リストの詳細取得に失敗しました");
    } finally {
      setFetchAllListDetailsRunning(false);
      setFetchAllListDetailsProgress(null);
    }
  };

  // ★ リストタブのデータだけを再取得（グラフ本体には触れない）
  const handleReloadListTab = async () => {
    if (!idToken || listTabReloading) return;
    setListTabReloading(true);
    try {
      await fetchBookLists();
      await Promise.all(
        expandedListIds.map(async (id) => {
          try {
            const res = await fetchListItems(id, idToken);
            setListItemsMap((prev) => ({ ...prev, [id]: res.items ?? [] }));
          } catch (e) {
            console.error("Error refreshing list items:", e);
          }
        })
      );
    } finally {
      setListTabReloading(false);
    }
  };

  // ★ お気に入り一覧の読み込み（100件ずつ。reset=true で先頭から取り直す）
  const loadFavorites = useCallback(
    async (reset: boolean) => {
      if (!idToken || loadingFavorites) return;
      setLoadingFavorites(true);
      try {
        const params = new URLSearchParams({ limit: "100" });
        const cursor = reset ? null : favoritesNextCursor;
        if (cursor) params.set("cursor", cursor);

        const res = await fetch(`${NEW_API.FAVORITES}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) throw new Error(`GET /favorites failed: ${res.status}`);

        const json = await res.json();
        const data = json.data ?? {};
        const items: FavoriteItem[] = data.items ?? [];

        setFavoriteItems((prev) => (reset ? items : [...prev, ...items]));
        setFavoritesNextCursor(data.next_cursor ?? null);
        setFavoritesLoadedOnce(true);
      } catch (e) {
        console.error("Error fetching favorites:", e);
      } finally {
        setLoadingFavorites(false);
      }
    },
    [idToken, loadingFavorites, favoritesNextCursor]
  );

  // お気に入りタブを開いたら初回だけ自動ロード
  useEffect(() => {
    if (
      leftPanelTab === "browse" &&
      browseMode === "favorites" &&
      isLoggedIn &&
      !favoritesLoadedOnce &&
      !loadingFavorites
    ) {
      loadFavorites(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftPanelTab, browseMode, isLoggedIn, favoritesLoadedOnce]);


  const handleNodeClick = useCallback((node: any) => {
    setSelectedNode(node);
  
    const fg: any = fgRef.current;
    if (!fg) return;
  
    // 3Dだけ：カメラ寄せ
    if (!is2DMode && typeof fg.cameraPosition === "function") {
      const distance = 240;
      const hyp = Math.hypot(node.x || 0, node.y || 0, node.z || 0);

      // 検索結果から追加した直後のノードは、フォースシミュレーションが
      // まだ1tickも回っておらず x/y/z が全て未設定（=0）のことがある。
      // その場合 distance/hyp が Infinity になり、カメラ座標が NaN になって
      // シーン全体が描画されなくなる（＝グラフが全部消えて見える）。
      // hyp が 0 のときは原点からの方向が定義できないので、
      // 固定の見下ろし位置にフォールバックする。
      const targetPosition = hyp === 0
        ? { x: 0, y: 0, z: distance }
        : {
            x: (node.x || 0) * (1 + distance / hyp),
            y: (node.y || 0) * (1 + distance / hyp),
            z: (node.z || 0) * (1 + distance / hyp),
          };

      fg.cameraPosition(targetPosition, node, 800);
      return;
    }
  
    // 2Dだけ：ズームして寄せる
    if (is2DMode && typeof fg.centerAt === "function") {
      const centerOnNode = () => {
        fg.centerAt(node.x || 0, node.y || 0, 250);
      };
      centerOnNode();
      if (typeof fg.zoom === "function") {
        fg.zoom(2.5, 500);
      }
      // 追加直後はシミュレーションがまだ収束しておらずノード座標が動き続けるため、
      // 一度きりの centerAt だと追加ノードが中心からズレて見える。
      // 収束するまでの短時間、追従して再センタリングする。
      [150, 300, 500, 800].forEach((ms) => setTimeout(centerOnNode, ms));
    }
  }, [is2DMode]);

  // ★ 書誌詳細パネル：選択中の本の著者一覧を取得（グラフに未表示の著者もボタンで出すため）
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (
        !selectedNode ||
        selectedNode.type !== "book" ||
        selectedNode.rlBookId == null
      ) {
        setPanelBookAuthors([]);
        return;
      }

      setPanelBookAuthorsLoading(true);
      try {
        const res = await fetchGlobalBookGraphOne(selectedNode.rlBookId);
        const userSet = new Set(userRlBookIds);
        const authors: GraphNode[] = [];
        for (const rawNode of res.nodes ?? []) {
          const normalized = normalizeGraphNode(rawNode, userSet);
          if (normalized && normalized.type === "author") {
            authors.push(normalized);
          }
        }
        if (!cancelled) setPanelBookAuthors(authors);
      } catch (e) {
        console.error("Error fetching book authors:", e);
        if (!cancelled) setPanelBookAuthors([]);
      } finally {
        if (!cancelled) setPanelBookAuthorsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedNode?.id, selectedNode?.type, selectedNode?.rlBookId, userRlBookIds]);

  // ★ 書誌詳細パネルの著者ボタン：クリックでグラフに追加（未追加なら）→ 中心に移動 → 選択状態に
  const handleAddAuthorToGraphById = async (tenkyoId: string) => {
    const existingNode = nodeMapRef.current.get(`author:${tenkyoId}`);
    if (existingNode) {
      handleNodeClick(existingNode);
      return;
    }

    try {
      const userSet = new Set(userRlBookIds);
      const res = await fetchAuthorityGraphOne(tenkyoId);

      let addedAuthor: GraphNode | null = null;

      for (const rawNode of res.nodes ?? []) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }

        if (
          !addedAuthor &&
          normalized.type === "author" &&
          (normalized.tenkyoId === tenkyoId ||
            normalized.id === `author:${tenkyoId}`)
        ) {
          addedAuthor = normalized;
        }
      }

      for (const link of res.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      if (addedAuthor) {
        setTimeout(() => handleNodeClick(addedAuthor as GraphNode), 300);
      }
    } catch (e) {
      console.error("Error adding author to graph:", e);
    }
  };

  // ★ 書誌詳細パネル：「全ての共著者の本を表示」
  // まだグラフに出ていない共著者だけをまとめて追加する（= 各著者ボタンを同時に押すのと同じ）
  const handleShowAllCoAuthorsBooks = async () => {
    const targets = panelBookAuthors.filter(
      (a) => a.tenkyoId && !nodeMapRef.current.has(`author:${a.tenkyoId}`)
    );
    if (targets.length === 0) return;

    const nodeToRecenter = selectedNode;

    try {
      setError(null);
      setLoadingGraph(true);

      const userSet = new Set(userRlBookIds);
      const results = await Promise.all(
        targets.map((a) => fetchAuthorityGraphOne(a.tenkyoId as string))
      );

      for (const res of results) {
        for (const rawNode of res.nodes ?? []) {
          const normalized = normalizeGraphNode(rawNode, userSet);
          if (!normalized) continue;

          const existing = nodeMapRef.current.get(normalized.id);
          if (existing) {
            existing.type = normalized.type;
            existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
            existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
            existing.title = normalized.title ?? existing.title;
            existing.name = normalized.name ?? existing.name;
            existing.label = normalized.label ?? existing.label;
            existing.raw = normalized.raw ?? existing.raw;
            existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
            existing.isInUserShelf =
              existing.isInUserShelf || normalized.isInUserShelf;
          } else {
            nodeMapRef.current.set(normalized.id, normalized);
          }
        }

        for (const link of res.links ?? []) {
          const key = makeLinkKey(link);
          if (!linkMapRef.current.has(key)) {
            linkMapRef.current.set(key, link);
          }
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });
    } catch (e) {
      console.error("Error showing all co-authors' books:", e);
      setError(
        e instanceof Error
          ? e.message
          : "共著者の本を表示中にエラーが発生しました"
      );
    } finally {
      setLoadingGraph(false);
      // 追加で新しいノードが増えると元の本が画面外に押し出されることがあるため、
      // 物理演算が少し落ち着くのを待ってから視点を選択中のノードへ戻す
      if (nodeToRecenter) {
        setTimeout(() => handleNodeClick(nodeToRecenter), 300);
      }
    }
  };

  // ★ 書誌詳細パネル：お気に入りトグル（書籍詳細ページと同じ挙動）
  const handleFavoriteToggle = async (rlBookId: number) => {
    if (!idToken) return;
    const isFav = favoritesState.favoriteBooks.some(
      (b) => b.rl_book_id === rlBookId
    );
    try {
      if (isFav) {
        await removeFavoriteBook(rlBookId, idToken);
        favoritesDispatch({ type: "REMOVE_FAVORITE", payload: rlBookId });
      } else {
        const res = await addFavoriteBook(rlBookId, idToken);
        favoritesDispatch({
          type: "ADD_FAVORITE",
          payload: { rl_book_id: rlBookId, favorited_at: res.data?.favorited_at },
        });
      }
    } catch (e) {
      console.error("Error toggling favorite:", e);
    }
  };



  const handleCancelAuthorExpand = (tenkyoId: string) => {
    setAuthorExpandCancelFor(tenkyoId);
    authorExpandCancelRef.current = tenkyoId;
  };

  // グラフを完全にクリアする処理
  const clearGraphCompletely = () => {
    // ★ Three.js テクスチャキャッシュを完全リセット
    if (typeof THREE !== "undefined" && THREE.Cache) {
      THREE.Cache.clear();
    }
  
    // 内部キャッシュも含めてリセット
    nodeMapRef.current = new Map();
    linkMapRef.current = new Map();
  
    setGraphData({ nodes: [], links: [] });
    setSelectedNode(null);
  
    // 展開状態などもリセット
    setExpandedBookRlBookIds([]);
    setExpandedAuthorTenkyoIds([]);
    setAuthorExpandProgress(null);
    setAuthorExpandCancelFor(null);
    authorExpandCancelRef.current = null;
  };
  

  const handleClickClearGraph = () => {
    setShowClearConfirm(true);
  };
  
  const handleCancelClearGraph = () => {
    setShowClearConfirm(false);
  };
  
  const handleConfirmClearGraph = () => {
    clearGraphCompletely();
    setShowClearConfirm(false);
  };
  

  // ==== 一覧用ユーティリティ（グラフタブ） ====
  const matchesFilter = (node: GraphNode, term: string) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;

    const fields: (string | undefined)[] = [];
    if (node.type === "book") {
      fields.push(
        node.title,
        node.rlBookId != null ? String(node.rlBookId) : undefined,
        node.isbn,
        node.publisher
      );
    } else {
      fields.push(node.name, node.tenkyoId);
    }
    fields.push(node.label, node.id);

    return fields.some((v) => v && v.toLowerCase().includes(q));
  };

  const {
    authorNodes,
    bookNodes,
    authorToBooks,
    bookToAuthors,
  } = useMemo(() => {
    const idToNode = new Map<string, GraphNode>();
    for (const n of graphData.nodes ?? []) {
      idToNode.set(n.id, n);
    }

    const authors: GraphNode[] = [];
    const books: GraphNode[] = [];

    for (const n of graphData.nodes ?? []) {
      if (n.type === "author") authors.push(n);
      else if (n.type === "book") books.push(n);
    }

    const a2b = new Map<string, GraphNode[]>();
    const b2a = new Map<string, GraphNode[]>();

    for (const link of graphData.links ?? []) {
      const srcId =
        typeof link.source === "string"
          ? link.source
          : (link.source as GraphNode).id;
      const dstId =
        typeof link.target === "string"
          ? link.target
          : (link.target as GraphNode).id;

      const src = idToNode.get(srcId);
      const dst = idToNode.get(dstId);
      if (!src || !dst) continue;

      if (src.type === "author" && dst.type === "book") {
        if (!a2b.has(src.id)) a2b.set(src.id, []);
        a2b.get(src.id)!.push(dst);

        if (!b2a.has(dst.id)) b2a.set(dst.id, []);
        b2a.get(dst.id)!.push(src);
      } else if (src.type === "book" && dst.type === "author") {
        if (!a2b.has(dst.id)) a2b.set(dst.id, []);
        a2b.get(dst.id)!.push(src);

        if (!b2a.has(src.id)) b2a.set(src.id, []);
        b2a.get(src.id)!.push(dst);
      }
    }

    for (const [aid, list] of a2b) {
      const uniq = Array.from(new Map(list.map((b) => [b.id, b])).values());
      a2b.set(aid, uniq);
    }
    for (const [bid, list] of b2a) {
      const uniq = Array.from(new Map(list.map((a) => [a.id, a])).values());
      b2a.set(bid, uniq);
    }

    authors.sort((a, b) =>
      (a.name ?? a.label ?? a.id).localeCompare(
        b.name ?? b.label ?? b.id,
        "ja"
      )
    );
    books.sort((a, b) =>
      (a.title ?? a.label ?? a.id).localeCompare(
        b.title ?? b.label ?? b.id,
        "ja"
      )
    );

    return {
      authorNodes: authors,
      bookNodes: books,
      authorToBooks: a2b,
      bookToAuthors: b2a,
    };
  }, [graphData]);

  const handleClosePanel = () => setSelectedNode(null);


  useEffect(() => {
    if (!is2DMode) return;
    if (!fgRef.current) return;
  
    // 次数を計算
    const deg = new Map<string, number>();
    for (const l of graphData.links ?? []) {
      const s = typeof l.source === "string" ? l.source : l.source.id;
      const t = typeof l.target === "string" ? l.target : l.target.id;
      deg.set(s, (deg.get(s) ?? 0) + 1);
      deg.set(t, (deg.get(t) ?? 0) + 1);
    }
  
    // ノードに collideR を付与（renderRは描画側のrと整合する値に）
    for (const n of graphData.nodes ?? []) {
      const d = deg.get(n.id) ?? 0;
  
      // 例：次数でちょい増やす（極端に増やさない）
      const degreeBoost = Math.min(10, Math.sqrt(d)) * 0.6; // 調整ポイント
  
      // 理想が 0.5 という話なら、paddingを小さく寄せる
      const padding = 0.5;
  
      // ここを「描画したい最大r」に揃える（上の r の max が 28 なら、ここもそれに）
      const base = n.type === "book" ? 14 : 7;
  
      n.renderR = base; // 目安（描画側で使わなくてもOK）
      n.collideR = base + degreeBoost + padding;
    }
  
    // collide force を当てる
    const fg: any = fgRef.current;
    fg.d3Force(
      "collide",
      d3.forceCollide().radius((d: any) => d.collideR ?? 10).iterations(2)
    );
  
    // 反映のためにリヒート
    fg.d3ReheatSimulation();
  }, [is2DMode, graphData]);

  const handleExpandAuthorAllBooks = async () => {
    if (!selectedNode || selectedNode.type !== "author") return;
  
    let tenkyoId =
      selectedNode.tenkyoId ??
      (selectedNode.id.startsWith("author:")
        ? selectedNode.id.split(":")[1]
        : selectedNode.id);
  
    if (!tenkyoId) return;
  
    if (expandedAuthorTenkyoIds.includes(tenkyoId)) {
      return;
    }
  
    try {
      setError(null);
      setLoadingGraph(true);
      setAuthorExpandCancelFor(null);
      authorExpandCancelRef.current = null;
  
      const userSet = new Set(userRlBookIds);

      // ① 著者起点のグラフ（＋本一覧）取得
      const authRes = await fetchAuthorityGraphOne(tenkyoId);

      // まず AuthorityGraphOne 自体をマージ
      for (const rawNode of authRes.nodes ?? []) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }
      }
      for (const link of authRes.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      // ② 対象本の分類
      const allBookRlBookIds = [...new Set(authRes.bookRlBookIds ?? [])];
      const coBooks = [...new Set(authRes.coauthorBookRlBookIds ?? [])];
      const soloBooks = [...new Set(authRes.soloBookRlBookIds ?? [])];

      if (allBookRlBookIds.length === 0) {
        // 本がそもそも無い
        setExpandedAuthorTenkyoIds((prev) =>
          prev.includes(tenkyoId) ? prev : [...prev, tenkyoId]
        );
        return;
      }

      const totalCount = allBookRlBookIds.length;

      // ③ 「探索済み」の初期値
      //  - すでに expandedBookRlBookIds に入っている本
      //  - 単著（soloBooks）の本（リンクを伸ばしても増えないので最初から探索済み扱い）
      let alreadyDoneCount = 0;
      const soloSet = new Set(soloBooks);
      const coSet = new Set(coBooks);

      const todoFromCo: number[] = [];

      for (const id of allBookRlBookIds) {
        if (soloSet.has(id)) {
          // 単著は探索済みにカウントするが、別途リンク展開リクエストは投げない
          alreadyDoneCount++;
        } else if (expandedBookRlBookIds.includes(id)) {
          // すでにリンク展開済みの本
          alreadyDoneCount++;
        } else if (coSet.has(id)) {
          // 共著本のうち、まだ展開していない本のみ TODO に入れる
          todoFromCo.push(id);
        } else {
          // co/solo のどちらか分からないデータが来た場合は保険で TODO 扱い
          todoFromCo.push(id);
        }
      }

      const todoRlBookIds = todoFromCo;
  
      setAuthorExpandProgress({
        tenkyoId,
        done: alreadyDoneCount,
        total: totalCount,
      });
  
      if (todoRlBookIds.length === 0) {
        // 追加で展開する本が無い場合
        setExpandedAuthorTenkyoIds((prev) =>
          prev.includes(tenkyoId) ? prev : [...prev, tenkyoId]
        );
        return;
      }

      // ④ 共著本のみ、1冊ずつ GetGraphWithDepth を叩いてリンクを伸ばす
      let doneCount = alreadyDoneCount;

      for (const rlBookId of todoRlBookIds) {
        if (authorExpandCancelRef.current === tenkyoId) {
          // ユーザーが途中で「止める」を押した場合
          return;
        }

        const res = await fetchBookGraphWithDepth(rlBookId, 2);

        for (const rawNode of res.nodes ?? []) {
          const normalized = normalizeGraphNode(rawNode, userSet);
          if (!normalized) continue;

          const existing = nodeMapRef.current.get(normalized.id);
          if (existing) {
            existing.type = normalized.type;
            existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
            existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
            existing.title = normalized.title ?? existing.title;
            existing.name = normalized.name ?? existing.name;
            existing.label = normalized.label ?? existing.label;
            existing.raw = normalized.raw ?? existing.raw;
            existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
            existing.isInUserShelf =
              existing.isInUserShelf || normalized.isInUserShelf;
          } else {
            nodeMapRef.current.set(normalized.id, normalized);
          }
        }

        for (const link of res.links ?? []) {
          const key = makeLinkKey(link);
          if (!linkMapRef.current.has(key)) {
            linkMapRef.current.set(key, link);
          }
        }

        setExpandedBookRlBookIds((prev) =>
          prev.includes(rlBookId) ? prev : [...prev, rlBookId]
        );
  
        doneCount++;
        setAuthorExpandProgress({
          tenkyoId,
          done: doneCount,
          total: totalCount,
        });
  
        setGraphData({
          nodes: Array.from(nodeMapRef.current.values()),
          links: Array.from(linkMapRef.current.values()),
        });
      }
  
      setExpandedAuthorTenkyoIds((prev) =>
        prev.includes(tenkyoId) ? prev : [...prev, tenkyoId]
      );
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error
          ? e.message
          : "著者のリンク展開中にエラーが発生しました"
      );
    } finally {
      setAuthorExpandProgress(null);
      setAuthorExpandCancelFor(null);
      authorExpandCancelRef.current = null;
      setLoadingGraph(false);
      // 展開で新しいノードが増えると元の著者が画面外に押し出されることがあるため、
      // 物理演算が少し落ち着くのを待ってから視点を選択中のノードへ戻す
      if (selectedNode) {
        const nodeToRecenter = selectedNode;
        setTimeout(() => handleNodeClick(nodeToRecenter), 300);
      }
    }
  };


  // ID から 1 冊追加
  const handleAddBookById = async () => {
    const inputId = idInput.trim();
    if (!inputId) return;
    const rlBookId = Number(inputId);
    if (isNaN(rlBookId)) return;

    try {
      setError(null);
      setIdSearchError(null);
      setLoadingGraph(true);

      const userSet = new Set(userRlBookIds);
      const res = await fetchGlobalBookGraphOne(rlBookId);

      if (!res.nodes || res.nodes.length === 0) {
        setIdSearchError("本が見つかりませんでした");
        return;
      }

      let addedBook: GraphNode | null = null;

      for (const rawNode of res.nodes) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }

        if (!addedBook && normalized.type === "book") {
          addedBook = normalized;
        }
      }

      for (const link of res.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      if (!addedBook) {
        setIdSearchError("本が見つかりませんでした");
        return;
      }

      setIdInput("");
      handleNodeClick(addedBook);
    } catch (e) {
      console.error(e);
      setIdSearchError("本が見つかりませんでした");
    } finally {
      setLoadingGraph(false);
    }
  };

  // ★ サジェスト結果から「本」をクリックしたとき
  const handleClickSuggestBook = async (item: SuggestBookResult) => {
    const rlBookId = item.rl_book_id;
    if (rlBookId == null) return;

    try {
      setError(null);
      setSearchError(null);
      setLoadingGraph(true);

      const userSet = new Set(userRlBookIds);
      const res = await fetchGlobalBookGraphOne(rlBookId);

      if (!res.nodes || res.nodes.length === 0) {
        setSearchError("本が見つかりませんでした");
        return;
      }

      let addedBook: GraphNode | null = null;

      for (const rawNode of res.nodes) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }

        if (
          !addedBook &&
          normalized.type === "book" &&
          (normalized.rlBookId === rlBookId ||
            normalized.id === String(rlBookId))
        ) {
          addedBook = normalized;
        }
      }

      for (const link of res.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      // 検索 UI をクリア
      setSearchText("");
      setSearchBooks([]);
      setSearchAuthors([]);

      if (addedBook) {
        setTimeout(() => handleNodeClick(addedBook as GraphNode), 300);
      } else {
        setSearchError("本が見つかりませんでした");
      }
    } catch (e) {
      console.error(e);
      setSearchError("本が見つかりませんでした");
    } finally {
      setLoadingGraph(false);
    }
  };

  // ★ サジェスト結果から「典拠」をクリックしたとき
  const handleClickSuggestAuthor = async (item: SuggestAuthorResult) => {
    const tenkyoId = item.tenkyo_id;
    if (!tenkyoId) return;

    try {
      setError(null);
      setSearchError(null);
      setLoadingGraph(true);

      const userSet = new Set(userRlBookIds);
      const res = await fetchAuthorityGraphOne(tenkyoId);

      let addedAuthor: GraphNode | null = null;

      for (const rawNode of res.nodes ?? []) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf =
            existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }

        if (
          !addedAuthor &&
          normalized.type === "author" &&
          (normalized.tenkyoId === tenkyoId ||
            normalized.id === tenkyoId ||
            normalized.id === `author:${tenkyoId}`)
        ) {
          addedAuthor = normalized;
        }
      }

      for (const link of res.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });

      // 検索 UI をクリア
      setSearchText("");
      setSearchBooks([]);
      setSearchAuthors([]);

      if (addedAuthor) {
        setTimeout(() => handleNodeClick(addedAuthor as GraphNode), 300);
      }
    } catch (e) {
      console.error(e);
      setSearchError(
        e instanceof Error
          ? e.message
          : "典拠の追加中にエラーが発生しました"
      );
    } finally {
      setLoadingGraph(false);
    }
  };

  // ★ Enter で「ID入力」検索（書誌→典拠の順で追加）
  const handleSearchEnter = async () => {
    const id = searchText.trim();
    if (!id) return;

    try {
      setError(null);
      setSearchError(null);
      setLoadingGraph(true);

      const userSet = new Set(userRlBookIds);

      // 1) まずは数値なら rl_book_id として試す
      const maybeRlBookId = Number(id);
      try {
        if (isNaN(maybeRlBookId)) throw new Error("not a number");
        const resBook = await fetchGlobalBookGraphOne(maybeRlBookId);
        if (resBook.nodes && resBook.nodes.length > 0) {
          let addedBook: GraphNode | null = null;

          for (const rawNode of resBook.nodes ?? []) {
            const normalized = normalizeGraphNode(rawNode, userSet);
            if (normalized && normalized.id) {
              if (!nodeMapRef.current.has(normalized.id)) {
                nodeMapRef.current.set(normalized.id, normalized);
              }
              if (!addedBook && normalized.type === 'book') {
                addedBook = normalized;
              }
            }
          }

          for (const link of resBook.links ?? []) {
            const key = makeLinkKey(link);
            if (!linkMapRef.current.has(key)) {
              linkMapRef.current.set(key, link);
            }
          }

          setGraphData({
            nodes: Array.from(nodeMapRef.current.values()),
            links: Array.from(linkMapRef.current.values()),
          });

          // 検索 UI をクリア
          setSearchText('');
          setSearchBooks([]);
          setSearchAuthors([]);

          if (addedBook) {
            setTimeout(() => handleNodeClick(addedBook as GraphNode), 300);
          }
          return;
        }
      } catch (e) {
        // 次の典拠検索へフォールバック（ここでは握りつぶす）
      }

      // 2) 次に典拠（TENKYO_ID）として試す
      try {
        const resAuth = await fetchAuthorityGraphOne(id);
        if (resAuth.nodes && resAuth.nodes.length > 0) {
          let addedAuthor: GraphNode | null = null;

          for (const rawNode of resAuth.nodes ?? []) {
            const normalized = normalizeGraphNode(rawNode, userSet);
            if (normalized && normalized.id) {
              if (!nodeMapRef.current.has(normalized.id)) {
                nodeMapRef.current.set(normalized.id, normalized);
              }
              if (!addedAuthor && normalized.type === 'author') {
                addedAuthor = normalized;
              }
            }
          }

          for (const link of resAuth.links ?? []) {
            const key = makeLinkKey(link);
            if (!linkMapRef.current.has(key)) {
              linkMapRef.current.set(key, link);
            }
          }

          setGraphData({
            nodes: Array.from(nodeMapRef.current.values()),
            links: Array.from(linkMapRef.current.values()),
          });

          // 検索 UI をクリア
          setSearchText('');
          setSearchBooks([]);
          setSearchAuthors([]);

          if (addedAuthor) {
            setTimeout(() => handleNodeClick(addedAuthor as GraphNode), 300);
          }
          return;
        }
      } catch (e) {
        // ID として解釈できなかっただけの可能性が高い（例: タイトルの文字列）。
        // 完全一致IDが無いのは通常の検索結果と同時にありうる普通の状態なので、
        // エラー表示はしない（下に出ている通常の検索候補で十分）。
      }
    } finally {
      setLoadingGraph(false);
    }
  };

  // ★ 本棚行クリック時（展開＋未ロードならロード）
  const handleClickShelfRow = async (shelf: UserHondana) => {
    const id = shelf.GlobalHondanaID;

    setExpandedShelfIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

    if (shelfBooksMap[id]) return;
    if (loadingShelfIds.includes(id)) return;

    try {
      setLoadingShelfIds((prev) => [...prev, id]);

      if (!isLoggedIn || !idToken || !userSub) return;
      // フェーズ1：DB割り当てだけを軽量取得。10万冊規模でも数秒で終わるため、
      // ここが終わった時点で一覧と正確な冊数をすぐ表示できる
      const books = await fetchUserHondanaBooksMinimal(id, idToken);
      setShelfBooksMap((prev) => ({ ...prev, [id]: books }));
      setLoadingShelfIds((prev) => prev.filter((x) => x !== id));

      // フェーズ2：書誌情報（タイトル等）のエンリッチをバックグラウンドで継続。
      // チャンクが返るたびに逐次反映するので、一覧はすでに触れる状態のまま
      // タイトルだけが後から埋まっていく
      if (books.length > 0) {
        setEnrichingShelfIds((prev) => [...prev, id]);
        try {
          await enrichShelfBooksDisplay(books, idToken, (updated) => {
            setShelfBooksMap((prev) => ({ ...prev, [id]: updated }));
          });
        } finally {
          setEnrichingShelfIds((prev) => prev.filter((x) => x !== id));
        }
      }
    } catch (e) {
      console.error(e);
      setError((prev) => prev ?? "本棚内の本の取得に失敗しました");
    } finally {
      setLoadingShelfIds((prev) => prev.filter((x) => x !== id));
    }
  };

  // ★ 本棚タブのデータだけを再取得（グラフ本体には触れない）
  const handleReloadShelfTab = async () => {
    if (!idToken || !userSub || shelfTabReloading) return;
    setShelfTabReloading(true);
    try {
      // 件数（スナップショット）を軽量に更新
      try {
        const snap = await refreshUserSnapshot(idToken);
        setSnapshotInfo(snap);
      } catch (e) {
        console.error("Error refreshing snapshot:", e);
      }

      // 本棚一覧を更新
      const list = await fetchUserHondanas(idToken);
      const sorted = [...list].sort(
        (a, b) => (a.ShelfOrder ?? 0) - (b.ShelfOrder ?? 0)
      );
      const mitorokuShelf: UserHondana = {
        GlobalHondanaID: "",
        HondanaName: "本棚未分類",
        UserSub: sorted[0]?.UserSub ?? userSub,
        ShelfOrder: 999999,
        OpenLevel: "PRIVATE",
        Description: null,
        ColorCode: "#4b5563",
      };
      setUserHondanas([...sorted, mitorokuShelf]);

      // 展開中の本棚は中身も更新（フェーズ1：割り当てのみ。タイトル等は後追いで反映）
      await Promise.all(
        expandedShelfIds.map(async (id) => {
          try {
            const books = await fetchUserHondanaBooksMinimal(id, idToken);
            setShelfBooksMap((prev) => ({ ...prev, [id]: books }));
            if (books.length > 0) {
              setEnrichingShelfIds((prev) => [...prev, id]);
              try {
                await enrichShelfBooksDisplay(books, idToken, (updated) => {
                  setShelfBooksMap((prev) => ({ ...prev, [id]: updated }));
                });
              } finally {
                setEnrichingShelfIds((prev) => prev.filter((x) => x !== id));
              }
            }
          } catch (e) {
            console.error("Error refreshing shelf books:", e);
          }
        })
      );
    } finally {
      setShelfTabReloading(false);
    }
  };

  // ★ 本棚チェック ON/OFF
  const handleToggleShelfSelected = (shelfId: string, checked: boolean) => {
    const books = shelfBooksMap[shelfId] ?? [];

    setSelectedShelfIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(shelfId);
      } else {
        set.delete(shelfId);
      }
      return Array.from(set);
    });

    if (books.length === 0) return;

    setSelectedShelfBookIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        for (const b of books) {
          set.add(b.GlobalBookID);
        }
      } else {
        for (const b of books) {
          set.delete(b.GlobalBookID);
        }
      }
      return Array.from(set);
    });
  };

  // ★ 本単位のチェック ON/OFF
  const handleToggleBookSelected = (
    shelfId: string,
    globalBookId: string,
    checked: boolean
  ) => {
    const books = shelfBooksMap[shelfId] ?? [];

    setSelectedShelfBookIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(globalBookId);
      } else {
        set.delete(globalBookId);
      }
      const next = Array.from(set);

      const allSelected =
        books.length > 0 && books.every((b) => set.has(b.GlobalBookID));

      setSelectedShelfIds((prevShelves) => {
        const s = new Set(prevShelves);
        if (allSelected) {
          s.add(shelfId);
        } else {
          s.delete(shelfId);
        }
        return Array.from(s);
      });

      return next;
    });
  };

  // ★ 複数選択ボタン
  const handleToggleShelfMultiSelect = () => {
    setShelfMultiSelectMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedShelfIds([]);
        setSelectedShelfBookIds([]);
      }
      return next;
    });
  };


  const applyBatchToGraph = useCallback(
    (batchRes: GraphResponse, userSet: Set<number>) => {
      for (const rawNode of batchRes.nodes ?? []) {
        const normalized = normalizeGraphNode(rawNode, userSet);
        if (!normalized) continue;

        const existing = nodeMapRef.current.get(normalized.id);
        if (existing) {
          existing.type = normalized.type;
          existing.rlBookId = normalized.rlBookId ?? existing.rlBookId;
          existing.tenkyoId = normalized.tenkyoId ?? existing.tenkyoId;
          existing.title = normalized.title ?? existing.title;
          existing.name = normalized.name ?? existing.name;
          existing.label = normalized.label ?? existing.label;
          existing.raw = normalized.raw ?? existing.raw;
          existing.coverUrl = normalized.coverUrl ?? existing.coverUrl;
          existing.isInUserShelf = existing.isInUserShelf || normalized.isInUserShelf;
        } else {
          nodeMapRef.current.set(normalized.id, normalized);
        }
      }

      for (const link of batchRes.links ?? []) {
        const key = makeLinkKey(link);
        if (!linkMapRef.current.has(key)) {
          linkMapRef.current.set(key, link);
        }
      }

      // 100冊ごとに更新
      setGraphData({
        nodes: Array.from(nodeMapRef.current.values()),
        links: Array.from(linkMapRef.current.values()),
      });
    },
    []
  );


  const handleAddSelectedShelfBooksToGraph = async () => {
    if (!shelfMultiSelectMode) return;
    if (selectedShelfBookIds.length === 0) return;
  
    try {
      setError(null);
      setLoadingGraph(true);
  
      const gbIdToBook = new Map<string, ShelfBook>();
      for (const books of Object.values(shelfBooksMap)) {
        for (const b of books) gbIdToBook.set(b.GlobalBookID, b);
      }
  
      const rlBookIdList: number[] = [];
      for (const gbId of selectedShelfBookIds) {
        const b = gbIdToBook.get(gbId);
        if (b?.rl_book_id != null) rlBookIdList.push(Number(b.rl_book_id));
      }

      const uniqueRlBookIds = [...new Set(rlBookIdList)];
      if (uniqueRlBookIds.length === 0) return;

      const userSet = new Set(userRlBookIds);

      // ★ run ガード（この処理が最新か判定）
      const runId = ++shelfBuildRunIdRef.current;
      const total = uniqueRlBookIds.length;
      setGraphBuildProgress({ source: "shelf", done: 0, total });

      await fetchGraphForRlBookIdBatch(
        uniqueRlBookIds,
        1,
        MAX_TOTAL_NODES,
        MAX_TOTAL_LINKS,
        (batchRes, batchIndex) => {
          if (shelfBuildRunIdRef.current !== runId) return;
  
          const done = Math.min((batchIndex + 1) * 100, total);
          applyBatchToGraph(batchRes, userSet);
  
          // ★ 最後は必ず消す
          if (done >= total) setGraphBuildProgress(null);
          else setGraphBuildProgress({ source: "shelf", done, total });
        }
      );
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "グラフ追加中にエラーが発生しました"
      );
    } finally {
      // ★ ここでも保険（別run開始済みなら消さない）
      // （最後の onBatch が飛んだ/例外が出た時に残るのを防ぐ）
      setLoadingGraph(false);
      if (graphBuildProgress && graphBuildProgress.done >= graphBuildProgress.total) {
        setGraphBuildProgress(null);
      }
  
      setSelectedShelfIds([]);
      setSelectedShelfBookIds([]);
      setShelfMultiSelectMode(false);
    }
  };

  const handleFetchAllShelfDetails = async () => {
    if (!isLoggedIn || !idToken) return;
    if (fetchAllShelfDetailsRunning) return;
    if (loadingSnapshot || loadingHondanas) return;
    if (!userHondanas || userHondanas.length === 0) return;
  
    setFetchAllShelfDetailsRunning(true);
    setError(null);
  
    const sorted = [...userHondanas].sort((a, b) => (a.ShelfOrder ?? 0) - (b.ShelfOrder ?? 0));
    const total = sorted.length;
  
    setFetchAllShelfDetailsProgress({ done: 0, total });
  
    try {
      for (let i = 0; i < sorted.length; i++) {
        const h = sorted[i];
        const id = h.GlobalHondanaID;
  
        // すでに取得済みならスキップ（要件的に「全部fetch」でもOKならこのifは外してOK）
        if (!shelfBooksMap[id]) {
          const books = await fetchUserHondanaBooksMinimal(id, idToken);
          setShelfBooksMap((prev) => ({ ...prev, [id]: books }));
          if (books.length > 0) {
            setEnrichingShelfIds((prev) => [...prev, id]);
            try {
              await enrichShelfBooksDisplay(books, idToken, (updated) => {
                setShelfBooksMap((prev) => ({ ...prev, [id]: updated }));
              });
            } finally {
              setEnrichingShelfIds((prev) => prev.filter((x) => x !== id));
            }
          }
        }
  
        setFetchAllShelfDetailsProgress({ done: i + 1, total });
  
        // UIに息継ぎ（任意）
        await new Promise((r) => setTimeout(r, 0));
        setAllShelfDetailsFetched(true);
      }
    } catch (e) {
      console.error(e);
      setError((prev) => prev ?? "本棚の詳細取得に失敗しました");
    } finally {
      setFetchAllShelfDetailsRunning(false);
      setFetchAllShelfDetailsProgress(null);
      
    }
  };
  
  


  useEffect(() => {
    let cancelled = false;
  
    const run = async () => {
      try {
        setError(null);
  
        // ✅ 認証が揃ってないなら、何もしない（初回の“空”で状態を壊さない）
        if (!isLoggedIn || !idToken || !userSub) {
          setSnapshotInfo(null);
          setUserRlBookIds([]);
          setGraphTargetRlBookIds([]);
          setGraphData({ nodes: [], links: [] });
          setLoadingSnapshot(false);
          setLoadingGraph(false);
          return;
        }
  
        setLoadingSnapshot(true);
  
        // ✅ refreshUserSnapshot が idToken/userSub を使うなら「引数で渡す」形に変えるのが安全
        const snapshot = await refreshUserSnapshot(idToken);
  
        if (cancelled) return;
        setSnapshotInfo(snapshot);
  
        console.log(
          `snapshot refreshed: ${snapshot.totalRlBookIds} 冊 / ${snapshot.chunks} チャンク`
        );

        if (snapshot.totalRlBookIds === 0 || snapshot.chunks === 0) {
          setUserRlBookIds([]);
          setGraphTargetRlBookIds([]);
          setGraphData({ nodes: [], links: [] });
          setLoadingSnapshot(false);
          setLoadingGraph(false);
          return;
        }
  
        // チャンク取得
        const allIds: number[] = [];
        for (let i = 0; i < snapshot.chunks; i++) {
          if (cancelled) return;
          try {
            const ids = await fetchSnapshotChunk(i, idToken);
            allIds.push(...ids);
          } catch (e) {
            console.error(e);
          }
        }

        if (cancelled) return;

        const uniqueIds = [...new Set(allIds)];
        setUserRlBookIds(uniqueIds);

        if (uniqueIds.length === 0) {
          setGraphTargetRlBookIds([]);
          setGraphData({ nodes: [], links: [] });
          setLoadingSnapshot(false);
          setLoadingGraph(false);
          return;
        }
  
        const limitedIds = uniqueIds.slice(0, MAX_GRAPH_SHELF_BOOKS);
        setGraphTargetRlBookIds(limitedIds);

        const batches = Math.ceil(limitedIds.length / GRAPH_BATCH_RL_BOOK_IDS);
        setMaxBatchCount(batches);
  
        nodeMapRef.current = new Map();
        linkMapRef.current = new Map();
        setGraphData({ nodes: [], links: [] });
        setNextBatchIndex(0);
  
        setLoadingSnapshot(false);

        // 以前はここで初回バッチを即時ロードし、ログイン時に本棚の本を
        // 自動でグラフに表示していたが、毎回本棚の本が全部載った状態で
        // 開始するのをやめたいという要望により削除。
        // スナップショット自体（userRlBookIds / graphTargetRlBookIds）は
        // 「本棚にない本を強調」等の機能で使うため引き続き取得しておき、
        // グラフへの実際の描画は検索・本棚タブからの手動追加に委ねる。
        setLoadingGraph(false);
      } catch (e: any) {
        console.error(e);
        if (cancelled) return;
        setError(e?.message ?? "エラーが発生しました");
        setLoadingSnapshot(false);
        setLoadingGraph(false);
      }
    };
  
    run();
  
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, idToken, userSub]);
  

// 認証状態が確定し、JWT が取れた後に本棚一覧を取得
useEffect(() => {
    // 認証がまだ or 未ログインなら何もしない
    if (!isLoggedIn || !idToken || !userSub) return;
  
    const loadHondanas = async () => {
      try {
        setLoadingHondanas(true);
  
        const list = await fetchUserHondanas(idToken);
  
        // ShelfOrder で並べ替え
        const sorted = [...list].sort(
          (a, b) => (a.ShelfOrder ?? 0) - (b.ShelfOrder ?? 0)
        );
  
        // 疑似本棚「本棚未分類」
        const mitorokuShelf: UserHondana = {
          GlobalHondanaID: "",
          HondanaName: "本棚未分類",
          UserSub: sorted[0]?.UserSub ?? userSub,
          ShelfOrder: 999999,
          OpenLevel: "PRIVATE",
          Description: null,
          ColorCode: "#4b5563",
        };
  
        setUserHondanas([...sorted, mitorokuShelf]);
      } catch (e) {
        console.error(e);
        setError((prev) => prev ?? "本棚一覧の取得に失敗しました");
      } finally {
        setLoadingHondanas(false);
      }
    };
  
    loadHondanas();
  }, [isLoggedIn, idToken, userSub]);
  

  // ★ サジェスト検索（タイトル・典拠名）
  useEffect(() => {
    const q = searchText.trim();

    if (!q) {
      setSearchLoading(false);
      setSearchError(null);
      setSearchBooks([]);
      setSearchAuthors([]);
      setSearchBookNextCursor(null);
      setIdMatchBook(null);
      setIdMatchAuthor(null);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    // 入力途中の古いリクエストが後から返ってきて結果を上書きしないよう、
    // このeffectが再実行（＝新しい文字が入力）されたら古いリクエストは中断・無視する
    let cancelled = false;
    const abortController = new AbortController();

    const handle = window.setTimeout(async () => {
      try {
        const res = await fetch(
          buildPrefixSearchUrl(q),
          { signal: abortController.signal }
        );
        if (!res.ok) {
          throw new Error(`Suggest API failed: ${res.status}`);
        }

        const data = (await res.json()) as Partial<SuggestResponse>;

        if (cancelled) return;
        setSearchBooks(
          Array.isArray(data.books) ? data.books : []
        );
        setSearchAuthors(
          Array.isArray(data.authors) ? data.authors : []
        );
        setSearchBookNextCursor(data.book_next_cursor ?? null);
      } catch (e) {
        if (cancelled || (e instanceof DOMException && e.name === "AbortError")) return;
        console.error(e);
        setSearchError(
          e instanceof Error
            ? e.message
            : "検索中にエラーが発生しました"
        );
        setSearchBooks([]);
        setSearchAuthors([]);
        setSearchBookNextCursor(null);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }

      // 入力が数字のみ（rl_book_id / tenkyo_id の形）のときだけ完全一致IDも探す。
      // 見つからない場合は何も表示しない（通常の検索結果はそのまま下に出ているため）。
      if (/^\d+$/.test(q)) {
        fetchGlobalBookGraphOne(Number(q))
          .then((res) => {
            if (cancelled) return;
            const node = (res.nodes ?? []).find(
              (n: any) => n.type === "book" && Number(n.rl_book_id) === Number(q)
            );
            setIdMatchBook(
              node
                ? {
                    rl_book_id: Number(q),
                    title: node.title ?? "",
                    volume: node.volume,
                    publisher: node.publisher,
                    responsibility: node.author,
                    publishing_date: node.pubYear != null ? String(node.pubYear) : undefined,
                    isbn13: node.isbn13,
                  }
                : null
            );
          })
          .catch(() => { if (!cancelled) setIdMatchBook(null); });

        fetchAuthorityGraphOne(q)
          .then((res) => {
            if (cancelled) return;
            const node = (res.nodes ?? []).find(
              (n: any) => n.type === "author" && (n.tenkyoId === q || n.tenkyoId === String(q))
            );
            setIdMatchAuthor(
              node
                ? {
                    tenkyo_id: q,
                    name: node.name ?? "",
                    reading: node.nameKana || node.nameRoman,
                    entity_type: node.entityType,
                    birth_death: node.dates,
                  }
                : null
            );
          })
          .catch(() => { if (!cancelled) setIdMatchAuthor(null); });
      } else if (!cancelled) {
        setIdMatchBook(null);
        setIdMatchAuthor(null);
      }
    }, 250);

    return () => {
      cancelled = true;
      abortController.abort();
      window.clearTimeout(handle);
    };
  }, [searchText]);

  // 検索候補内・書籍の「もっと見る」（searchBookNextCursorがnullの間は呼ばれない）
  const handleLoadMoreSearchBooks = async () => {
    const q = searchText.trim();
    if (!q || !searchBookNextCursor || loadingMoreSearchBooks) return;
    setLoadingMoreSearchBooks(true);
    try {
      const res = await fetch(buildPrefixSearchUrl(q, 20, 0, searchBookNextCursor));
      if (!res.ok) throw new Error(`Suggest API failed: ${res.status}`);
      const data = (await res.json()) as Partial<SuggestResponse>;
      setSearchBooks((prev) => [...prev, ...(Array.isArray(data.books) ? data.books : [])]);
      setSearchBookNextCursor(data.book_next_cursor ?? null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMoreSearchBooks(false);
    }
  };

  // 自動ロード
  useEffect(() => {
    if (!autoLoad) return;

    if (nextBatchIndex >= maxBatchCount) {
      setAutoLoad(false);
      return;
    }

    const timerId = window.setTimeout(() => {
      loadNextBatch(graphTargetRlBookIds, nextBatchIndex, new Set(userRlBookIds));
    }, 30000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [autoLoad, nextBatchIndex, maxBatchCount, graphTargetRlBookIds, userRlBookIds]);

  
  const nodeCanvasObject = useMemo(() => {
    return (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const isSelected = selectedNode?.id === node.id;
      const isHovered = !isSelected && hoveredNodeId === node.id;
      const isBook = node.type === "book";
      const isAuthor = node.type === "author";
  
      const isShelf = !!node.isInUserShelf;
      const dimNonTargetBook =
        isBook &&
        ((shelfHighlightMode === 1 && !isShelf) ||
          (shelfHighlightMode === 2 && isShelf));
  
      const coverUrl = isBook ? getCoverUrl(node) : undefined;
  
      // const baseR = isBook ? 12 : 6;
      const baseR = isBook ? 14 : 7;
      // const r =
      //   (isSelected ? baseR * 1.25 : baseR) / Math.max(1, globalScale * 1);
      const rawR = (isSelected ? baseR * 1.25 : baseR) / Math.max(1, globalScale * 1.25);

      const r = Math.min(28, Math.max(isBook ? 6 : 4, rawR));
  
      const x = node.x ?? 0;
      const y = node.y ?? 0;

      // 書影が既に読み込み済みかどうかを先に判定しておく。読み込み済みなら
      // 後段で書影の四角形をそのまま描くため、丸いベースプレースホルダーは
      // 不要（前は常に丸を描いてから書影を上に重ねていたため、縦長の書影の
      // 左右から丸がはみ出て見える不具合があった。2026-08-29対応）
      let coverImg: HTMLImageElement | undefined;
      if (coverUrl) {
        const cache = coverImgCacheRef.current;
        let img = cache.get(coverUrl);

        if (!img) {
          img = new Image();
          cache.set(coverUrl, img);
          const target = img;
          scheduleCoverLoad(() => {
            target.onload = () => finishCoverLoad();
            target.onerror = () => {
              // 失敗した書影はキャッシュから外し、再描画時にリトライできるようにする
              cache.delete(coverUrl);
              finishCoverLoad();
            };
            target.src = coverUrl;
          });
        }

        if (img.complete && img.naturalWidth > 0) {
          coverImg = img;
        }
      }

      // ===== ベースの背景円（プレースホルダー。書影が既に描ける場合は不要）=====
      if (!coverImg) {
        ctx.save();
        if (dimNonTargetBook) ctx.globalAlpha = 0.22;

        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        // ライトモードはキャンバス背景が明るいクリーム色のため、ダークモードと
        // 同じ淡いグレーだとコントラストが弱く見づらい。ライトモードのみ
        // ワントーン濃くする（2026-08-29）。
        ctx.fillStyle = isSelected
          ? "#facc15"
          : isHovered
          ? (isBook
              ? (themeMode === "light" ? "#334155" : "#475569")
              : (themeMode === "light" ? "#64748b" : "#94a3b8"))
          : isBook
          ? (themeMode === "light" ? "#1e293b" : "#334155")
          : (themeMode === "light" ? "#475569" : "#64748b");
        ctx.fill();
        ctx.restore();
      }

      // ===== 書影（bookのみ）=====
      {
        const img = coverImg;
        if (img) {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const scale = (r * 2) / Math.max(w, h);
          const dw = w * scale;
          const dh = h * scale;
  
          ctx.save();
  
          // 本棚外の本は薄く（強調モード）
          if (dimNonTargetBook) ctx.globalAlpha = 0.22;
  
          // 書影の実際の縦横比に合わせた四角形でクリップして描画
          // （以前は丸クリップだったが、書影は四角いものなので形を合わせる）
          ctx.beginPath();
          ctx.rect(x - dw / 2, y - dh / 2, dw, dh);
          ctx.clip();
          ctx.drawImage(img, x - dw / 2, y - dh / 2, dw, dh);
          ctx.restore();
  
          // ===== 輪郭線（暗めグレーに）=====
          ctx.save();
          ctx.beginPath();
          ctx.rect(x - dw / 2, y - dh / 2, dw, dh);
          ctx.lineWidth = isHovered
            ? Math.max(1.5, 3 / globalScale)
            : Math.max(1, 2 / globalScale);

          if (isHovered) {
            // マウスオーバー中は目立つ色でリングを強調
            ctx.strokeStyle = "#38bdf8";
          } else if (dimNonTargetBook) {
            // 暗めグレー（要求どおり）
            ctx.strokeStyle = "rgba(100,116,139,0.35)";
          } else {
            // 通常時（少し明るめ）
            ctx.strokeStyle = TOKENS.canvasOutline;
          }

          ctx.stroke();
          ctx.restore();
        }
      }
  
      // ===== ラベル（本・著者とも常に表示） =====
      const label = isAuthor
        ? node.name ?? node.label ?? node.id
        : node.title
        ? titleWithVolume(node)
        : node.label ?? node.id;

      const fontSize = Math.max(3, 12 / globalScale);
      ctx.save();

      // 本棚外の本ラベルはさらに薄く（ただしauthorは薄くしない）
      if (dimNonTargetBook) ctx.globalAlpha = 0.35;

      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = TOKENS.canvasLabelText;
      ctx.fillText(label, x + r + 4, y);

      ctx.restore();
    };
  }, [
    selectedNode,
    hoveredNodeId,
    getCoverUrl,
    is2DMode,
    shelfHighlightMode, // ★ 0:none / 1:in-shelf / 2:out-of-shelf
    TOKENS,
    themeMode,
  ]);

  // ★ 2D/3D共通：パーティクルの流れる向きを本→典拠から典拠→本に見せるため、
  // source/target を入れ替えた別配列を作る（force-graph の linkSource/linkTarget
  // プロパティは内部で link.source を上書きしてから link.target を読むため、
  // 同名フィールド同士のswapに使うと両方が同じノードに潰れてしまう。
  // そのため graphData そのものは変更せず、ここで複製してから入れ替える）
  const graphDataReversed = useMemo(
    () => ({
      nodes: graphData.nodes,
      links: graphData.links.map((l) => ({
        ...l,
        source: l.target,
        target: l.source,
      })),
    }),
    [graphData]
  );



  const graphProps = useMemo(
    () => ({
      graphData: graphDataReversed,
      // hover tooltip からID表示を外す
      nodeLabel: (n: GraphNode) =>
        n.type === "book"
          ? n.title
            ? titleWithVolume(n)
            : (n.label ?? "")
          : (n.name ?? n.label ?? ""),
      // alias（同一著者の別名）リンクは太い静止線にして、
      // 著作関係（authorship）の流れるパーティクル演出と区別する
      linkWidth: (l: GraphLink) => (l.type === "alias" ? 4 : 1),
      linkDirectionalParticles: (l: GraphLink) => (l.type === "alias" ? 0 : 2),
      linkDirectionalParticleSpeed: 0.001,
      backgroundColor: TOKENS.canvasBg,
      // ノード追加のたびに毎回長々と揺れ続けるのを防ぐため、収束を大幅に速める
      // （デフォルトのd3AlphaDecay=0.0228だと落ち着くまで約5秒かかる）
      d3AlphaDecay: 0.08,
      d3VelocityDecay: 0.5,
      d3AlphaMin: 0.01,
      nodeThreeObject: (n: any) => {
        const node = n as GraphNode;

        if (node.type === "book") {
          const isShelf = !!node.isInUserShelf;
          const cover = getCoverUrl(node);

          const isSelected =
            selectedNode &&
            selectedNode.id === node.id &&
            selectedNode.type === "book";
          const isHovered = !isSelected && hoveredNodeId === node.id;

          if (cover) {
            const tex = getTexture(cover);
            const material = new THREE.SpriteMaterial({
              map: tex,
              transparent: true,
              opacity:
                (shelfHighlightMode === 1 && !isShelf) ||
                (shelfHighlightMode === 2 && isShelf)
                  ? 0.15
                  : 1,
              color: 0xffffff,
            });

            const baseHeight = 18;
            const baseWidth = 12;
            const scaleFactor = 1.0;

            const sprite = new THREE.Sprite(material);
            sprite.scale.set(
              baseWidth * scaleFactor,
              baseHeight * scaleFactor,
              1
            );

            if (isSelected) {
              const borderCanvas = document.createElement("canvas");
              const borderSize = 200;
              borderCanvas.width = borderSize;
              borderCanvas.height = borderSize;

              const ctx = borderCanvas.getContext("2d")!;
              ctx.clearRect(0, 0, borderSize, borderSize);

              ctx.strokeStyle = TOKENS.selectionRing;
              ctx.lineWidth = 8;
              ctx.setLineDash([14, 10]);
              ctx.strokeRect(6, 6, borderSize - 12, borderSize - 12);

              const borderTexture = new THREE.CanvasTexture(borderCanvas);
              const borderMaterial = new THREE.SpriteMaterial({
                map: borderTexture,
                transparent: true,
                depthTest: false,
                depthWrite: false,
              });

              const borderSprite = new THREE.Sprite(borderMaterial);

              const borderScale = 1.22;
              borderSprite.scale.set(
                baseWidth * borderScale,
                baseHeight * borderScale,
                1
              );

              borderSprite.position.set(0, 0, -0.01);

              // 「動く点線」アニメーション用に、この選択枠のcanvas/textureを登録しておく
              selectedBorderAnimRef.current = { ctx, texture: borderTexture, size: borderSize };

              const group = new THREE.Group();
              group.add(borderSprite);
              group.add(sprite);

              return group;
            }

            if (isHovered) {
              // 選択時より控えめな色・太さで「マウスオーバー中」を示す
              const borderCanvas = document.createElement("canvas");
              const borderSize = 200;
              borderCanvas.width = borderSize;
              borderCanvas.height = borderSize;

              const ctx = borderCanvas.getContext("2d")!;
              ctx.clearRect(0, 0, borderSize, borderSize);

              ctx.strokeStyle = "#38bdf8";
              ctx.lineWidth = 8;
              ctx.strokeRect(6, 6, borderSize - 12, borderSize - 12);

              const borderTexture = new THREE.CanvasTexture(borderCanvas);
              const borderMaterial = new THREE.SpriteMaterial({
                map: borderTexture,
                transparent: true,
                depthTest: false,
                depthWrite: false,
              });

              const borderSprite = new THREE.Sprite(borderMaterial);

              const borderScale = 1.15;
              borderSprite.scale.set(
                baseWidth * borderScale,
                baseHeight * borderScale,
                1
              );

              borderSprite.position.set(0, 0, -0.01);

              const group = new THREE.Group();
              group.add(borderSprite);
              group.add(sprite);

              return group;
            }

            return sprite;
          }

          const geom = new THREE.SphereGeometry(6, 16, 16);
          const mat = new THREE.MeshBasicMaterial({
            color: isSelected
              ? 0xfacc15
              : isHovered
              ? 0x38bdf8
              : isShelf
              ? 0x22c55e
              : 0x3b82f6,
            opacity:
              (shelfHighlightMode === 1 && !isShelf) ||
              (shelfHighlightMode === 2 && isShelf)
                ? 0.2
                : 1,
            transparent: true,
          });
          if (isSelected) {
            geom.scale(1.3, 1.3, 1.3);
          } else if (isHovered) {
            geom.scale(1.15, 1.15, 1.15);
          }
          return new THREE.Mesh(geom, mat);
        }

        if (node.type === "author") {
          const isAuthorHovered = hoveredNodeId === node.id;
          return createTextSprite(
            node.name ?? node.label ?? node.id,
            isAuthorHovered ? "#38bdf8" : TOKENS.canvasLabelText
          );
        }

        return undefined;
      },
    }),
    [graphDataReversed, shelfHighlightMode, selectedNode, hoveredNodeId, TOKENS]
  );

  // 3Dモード：選択中ノードの枠を「動く点線」にするアニメーションループ
  useEffect(() => {
    if (is2DMode || !selectedNode) return;

    let rafId: number;
    let dashOffset = 0;

    const tick = () => {
      const target = selectedBorderAnimRef.current;
      if (target) {
        const { ctx, texture, size } = target;
        dashOffset -= 0.6;
        ctx.clearRect(0, 0, size, size);
        ctx.strokeStyle = TOKENS.selectionRing;
        ctx.lineWidth = 8;
        ctx.setLineDash([14, 10]);
        ctx.lineDashOffset = dashOffset;
        ctx.strokeRect(6, 6, size - 12, size - 12);
        texture.needsUpdate = true;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [is2DMode, selectedNode, TOKENS]);

  // 完全一致IDが見つかっていれば、通常の検索候補と同じデザインで先頭に表示する
  const displaySearchAuthors = idMatchAuthor
    ? [idMatchAuthor, ...searchAuthors.filter((a) => a.tenkyo_id !== idMatchAuthor.tenkyo_id)]
    : searchAuthors;
  const displaySearchBooks = idMatchBook
    ? [idMatchBook, ...searchBooks.filter((b) => b.rl_book_id !== idMatchBook.rl_book_id)]
    : searchBooks;

  return (
    <Box sx = {{mt:7}} >
    <main
      ref={graphContainerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "calc(100vh - 56px)",
        background: TOKENS.canvasBg,
        overflow: "hidden",
      }}
    >

    {/* グラフ（2D / 3D） */}
    {is2DMode ? (
      <ForceGraph2D
        ref={fgRef as any}
        width={graphSize.width}
        height={graphSize.height}
        graphData={graphDataReversed as any}
        onNodeClick={handleNodeClick as any}
        onNodeHover={handleNodeHover as any}
        backgroundColor={TOKENS.canvasBg}
        nodeCanvasObject={nodeCanvasObject as any}
        // 既存の色や線の雰囲気に寄せる
        linkColor={() => TOKENS.canvasLinkColor}
        linkWidth={(l: GraphLink) => (l.type === "alias" ? 4 : 1)}
        // パーティクルが多すぎる見た目にならないよう、リンクの一部（約1/5）にだけ出す
        linkDirectionalParticles={(l: GraphLink) =>
          l.type === "alias"
            ? 0
            : hashStringToInt(makeLinkKey(l)) % 5 === 0
            ? 2
            : 0
        }
        linkDirectionalParticleSpeed={0.001}
        // ノード追加のたびに毎回長々と揺れ続けるのを防ぐため、収束を大幅に速める
        d3AlphaDecay={0.08}
        d3VelocityDecay={0.5}
        d3AlphaMin={0.01}
      />
    ) : (
      <ForceGraph3D
        ref={fgRef as any}
        {...(graphProps as any)}
        width={graphSize.width}
        height={graphSize.height}
        onNodeClick={handleNodeClick as any}
        onNodeHover={handleNodeHover as any}
        onEngineTick={() => {
          if (!autoLoad || !fgRef.current) return;

          const fg = fgRef.current;
          const radius = 1200;
          const height = 80;
          const speed = -0.004;

          orbitAngleRef.current += speed;

          const x = radius * Math.cos(orbitAngleRef.current);
          const z = radius * Math.sin(orbitAngleRef.current);

          fg.cameraPosition({ x, y: height, z }, { x: 0, y: 0, z: 0 }, 0);
        }}
      />
    )}



    {/* 左上ステータス（ボタンではないため、枠や背景を持たせず素のテキストとして表示） */}
    <div style={
      {
          position: "absolute",
          top: 12,
          left: 12,
          color: TOKENS.textSub,
          fontSize: 11,
          display: "flex",
          alignItems: "center",
          gap: 8,
          zIndex: 30,
        }}>
      <span>
        表示中 典拠:{visibleAuthorCount} 本:{visibleBookCount}冊
      </span>

      {graphBuildProgress ? (
        <span>
          {" "}
          {graphBuildProgress.source === "shelf"
            ? `本棚からグラフ構築中 ${graphBuildProgress.done}/${graphBuildProgress.total} ...`
            : graphBuildProgress.source === "list"
            ? `リストからグラフ構築中 ${graphBuildProgress.done}/${graphBuildProgress.total} ...`
            : `グラフ構築中 ${graphBuildProgress.done}/${graphBuildProgress.total} ...`}
        </span>
      ) : (
        loadingGraph && <span> グラフ構築中…</span>
      )}

      {error && <span style={{ color: TOKENS.error }}>Error: {error}</span>}
    </div>


    {/* タイトル・典拠名で検索（サジェスト）。右上の「書影を表示」ボタン分の余白を
        確保してもなお画面中央に置けるだけの横幅があれば画面中央に、無ければ
        従来通り左寄り（右ボタンとの重なり回避を優先）に配置する。 */}
    <div
      style={{
        position: "absolute",
        top: 52,
        left: 12,
        right: canCenterSearchBar ? 12 : SEARCH_BAR_RIGHT_GAP,
        zIndex: 35,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: SEARCH_BAR_MAX_WIDTH }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="タイトル・典拠名で検索"
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearchEnter();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "5px 14px",
            fontSize: 13,
            borderRadius: 999,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glassStrong,
            color: TOKENS.text,
            outline: "none",
          }}
        />

        {/* 検索ヒント（?アイコン。ホバー/クリックで説明を表示） */}
        <div
          style={{ position: "relative", flexShrink: 0, width: 28 }}
          onMouseEnter={() => setSearchHintOpen(true)}
          onMouseLeave={() => setSearchHintOpen(false)}
        >
          <button
            onClick={() => setSearchHintOpen((v) => !v)}
            title="検索のヒント"
            style={{
              width: 22,
              height: 22,
              padding: 0,
              borderRadius: "50%",
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.glassStrong,
              color: TOKENS.textSub,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ?
          </button>
          {searchHintOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 6,
                width: 230,
                padding: 8,
                fontSize: 10.5,
                lineHeight: 1.6,
                borderRadius: 8,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.glassStrong,
                color: TOKENS.text,
                boxShadow: "0 12px 24px rgba(0,0,0,0.6)",
                zIndex: 40,
              }}
            >
              典拠名・著書名の検索ヒント：1文字および2文字のタイトルは完全一致する必要があります。また3文字以上のタイトルを探すには、3文字以上入力する必要があります。
            </div>
          )}
        </div>
      </div>
      {searchText.trim() && isSearchFocused && (
        <div
          style={{
            marginTop: 4,
            maxHeight: 260,
            overflowY: "auto",
            borderRadius: 8,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glassStrong,
            boxShadow: "0 12px 24px rgba(0,0,0,0.6)",
            padding: 6,
            fontSize: 11,
          }}
        >
          {searchLoading && (
            <div style={{ color: TOKENS.textSub, padding: "4px 2px" }}>
              検索中…
            </div>
          )}
          {searchError && !searchLoading && (
            <div style={{ color: TOKENS.error, padding: "4px 2px" }}>
              {searchError}
            </div>
          )}
          {!searchLoading &&
            !searchError &&
            displaySearchAuthors.length === 0 &&
            displaySearchBooks.length === 0 && (
              <div style={{ color: TOKENS.textSub, padding: "4px 2px" }}>
                該当する結果がありません。
              </div>
            )}

          {displaySearchAuthors.length > 0 && (
            <div style={{ marginBottom: displaySearchBooks.length > 0 ? 6 : 0 }}>
              <div
                style={{
                  fontSize: 10,
                  color: TOKENS.textSub,
                  marginBottom: 2,
                }}
              >
                典拠
              </div>
              {displaySearchAuthors.map((a) => {
                const suggestKey = `author:${a.tenkyo_id}`;
                const isHovered = hoveredSuggestKey === suggestKey;
                return (
                <div
                  key={a.tenkyo_id}
                  onClick={() => handleClickSuggestAuthor(a)}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHoveredSuggestKey(suggestKey)}
                  onMouseLeave={() =>
                    setHoveredSuggestKey((k) => (k === suggestKey ? null : k))
                  }
                  style={{
                    padding: "4px 4px",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    border: `1px solid ${isHovered ? TOKENS.accent : "transparent"}`,
                    background: isHovered ? TOKENS.cardBgHover : "transparent",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: TOKENS.text,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {a.name.replace(/,\s*/g, " ")}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: TOKENS.textSub,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {[
                      a.reading,
                      a.birth_death,
                    ]
                      .filter(Boolean)
                      .join(" / ")}
                  </span>
                </div>
                );
              })}
            </div>
          )}

          {displaySearchBooks.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  color: TOKENS.textSub,
                  marginBottom: 2,
                }}
              >
                本
              </div>
              {displaySearchBooks.map((b) => {
                const suggestKey = `book:${b.rl_book_id}`;
                const isHovered = hoveredSuggestKey === suggestKey;
                return (
                <div
                  key={b.rl_book_id}
                  onClick={() => handleClickSuggestBook(b)}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHoveredSuggestKey(suggestKey)}
                  onMouseLeave={() =>
                    setHoveredSuggestKey((k) => (k === suggestKey ? null : k))
                  }
                  style={{
                    padding: "4px 4px",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    border: `1px solid ${isHovered ? TOKENS.accent : "transparent"}`,
                    background: isHovered ? TOKENS.cardBgHover : "transparent",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: TOKENS.text,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {titleWithVolume(b)}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: TOKENS.textSub,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {[
                      b.responsibility,
                      b.publisher,
                      b.publishing_date
                    ]
                      .filter(Boolean)
                      .join(" / ")}
                  </span>
                </div>
                );
              })}
              {searchBookNextCursor && (
                <div
                  onClick={handleLoadMoreSearchBooks}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    padding: "5px 4px",
                    textAlign: "center",
                    fontSize: 10.5,
                    color: loadingMoreSearchBooks ? TOKENS.textSub : TOKENS.accent,
                    cursor: loadingMoreSearchBooks ? "default" : "pointer",
                  }}
                >
                  {loadingMoreSearchBooks ? "読み込み中…" : "もっと見る"}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>

    {idSearchError && (
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 252,
          zIndex: 30,
          fontSize: 10,
          color: TOKENS.error,
        }}
      >
        {idSearchError}
      </div>
    )}

    {/* 本棚強調モード（0:なし / 1:本棚 / 2:本棚以外） */}
    <button
      onClick={() =>
        setShelfHighlightMode((m) => (m === 0 ? 1 : m === 1 ? 2 : 0))
      }
      style={{
        position: "absolute",
        top: 12,
        // 右サイドパネルの開閉に連動させない（常に固定。パネルの下に隠れてよい）。
        // 連動させると狭い画面で左側の検索欄と干渉していたため（2026-08-29）
        right: 12,
        zIndex: 30,
        padding: "6px 10px",
        fontSize: 11,
        borderRadius: 999,
        border: `1px solid ${TOKENS.border}`,
        background: TOKENS.glassStrong,
        color: TOKENS.text,
        cursor: "pointer",
      }}
    >
      {shelfHighlightMode === 0
        ? "蔵書有無による強調表示なし"
        : shelfHighlightMode === 1
        ? "蔵書あり：強調中"
        : "蔵書なし：強調中"}
    </button>

      {/* 右上：2D/3D トグル */}
      <div
        style={{
            position: "absolute",
            top: 44,
            right: 12,
            zIndex: 40,
            display: "flex",
            gap: 8,
            alignItems: "center",
        }}
        >
        <button
            onClick={() => setIs2DMode((v) => !v)}
            style={{
            padding: "6px 10px",
            borderRadius: 999,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glassStrong,
            color: TOKENS.text,
            fontSize: 11,
            cursor: "pointer",
            }}
            title="2D / 3D 切り替え"
        >
            {is2DMode ? "3Dに切替" : "2Dに切替"}
        </button>
      </div>

      {/* 左側パネル（スマホ向けに折りたたみ可能） */}
      {!isLeftPanelCollapsed ? (
      <div
        style={{
          position: "absolute",
          top: 88,
          left: 12,
          bottom: 12,
          width: "min(280px, 88vw)",
          background: TOKENS.glassStrong,
          borderRadius: 12,
          border: `1px solid ${TOKENS.border}`,
          padding: 8,
          color: TOKENS.text,
          fontSize: 11,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          zIndex: 22,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* タブ */}
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 4,
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 2,
              flex: isLoggedIn ? 1 : "0 0 auto",
              background: TOKENS.cardBg,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: 999,
              padding: 2,
              minWidth: 0,
            }}
          >
            {/* 表示中の典拠と本（常に表示。未ログイン時はこれだけを左詰めで表示） */}
            <button
              onClick={() => setLeftPanelTab("graph")}
              style={{
                flex: isLoggedIn ? "1 1 0" : "0 0 auto",
                minWidth: 0,
                padding: "5px 8px",
                borderRadius: 999,
                border: "none",
                background:
                  leftPanelTab === "graph" ? TOKENS.buttonBgHover : "transparent",
                color: leftPanelTab === "graph" ? TOKENS.text : TOKENS.textSub,
                fontSize: 11,
                fontWeight: leftPanelTab === "graph" ? 700 : 400,
                boxShadow:
                  leftPanelTab === "graph"
                    ? `0 1px 3px ${TOKENS.border}`
                    : "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.15s, font-weight 0.15s",
              }}
            >
              表示中の典拠と本
            </button>

            {/* 本棚 | リスト | お気に入り（ログイン時のみ表示するプルダウン） */}
            {isLoggedIn && (
              <select
                value={browseMode}
                onChange={(e) => {
                  setBrowseMode(e.target.value as "shelf" | "list" | "favorites");
                  setLeftPanelTab("browse");
                }}
                onClick={() => setLeftPanelTab("browse")}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  padding: "5px 6px",
                  borderRadius: 999,
                  border: "none",
                  background:
                    leftPanelTab === "browse" ? TOKENS.buttonBgHover : "transparent",
                  color: leftPanelTab === "browse" ? TOKENS.text : TOKENS.textSub,
                  fontSize: 11,
                  fontWeight: leftPanelTab === "browse" ? 700 : 400,
                  boxShadow:
                    leftPanelTab === "browse"
                      ? `0 1px 3px ${TOKENS.border}`
                      : "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  WebkitAppearance: "none",
                  appearance: "none",
                  transition: "background 0.15s, font-weight 0.15s",
                }}
              >
                <option value="shelf">本棚 ▾</option>
                <option value="list">リスト ▾</option>
                <option value="favorites">お気に入り ▾</option>
              </select>
            )}
          </div>

          <button
            onClick={() => setIsLeftPanelCollapsed(true)}
            style={{
              padding: "4px 10px",
              fontSize: 11,
              borderRadius: 999,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.glassStrong,
              color: TOKENS.text,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title="左パネルを畳む"
          >
            ◀
          </button>
        </div>

        {/* 本棚タブ */}
        
        {leftPanelTab === "browse" && browseMode === "shelf" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >

            {/* ステータス */}

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div>
                {!isLoggedIn
                  ? "ログインすると本棚を表示できます"
                  : loadingSnapshot || loadingHondanas
                  ? "本棚: Loading…"
                  : snapshotInfo
                  ? `本棚: ${snapshotInfo.totalRlBookIds} 冊`
                  : "本棚: -"}
              </div>

              {isLoggedIn && idToken && (
                <button
                  onClick={handleReloadShelfTab}
                  disabled={shelfTabReloading}
                  title="本棚データを更新"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    padding: 0,
                    borderRadius: 999,
                    border: "none",
                    background: "transparent",
                    color: TOKENS.textSub,
                    cursor: shelfTabReloading ? "default" : "pointer",
                  }}
                >
                  <RefreshIcon
                    style={{
                      fontSize: 14,
                      animation: shelfTabReloading
                        ? "rl-spin 0.8s linear infinite"
                        : undefined,
                    }}
                  />
                </button>
              )}

              {/* ★ 未取得 or 取得中 のときだけ表示 */}
              {(!allShelfDetailsFetched || fetchAllShelfDetailsRunning) && (
                <>
                  <button
                    onClick={handleFetchAllShelfDetails}
                    disabled={
                      !isLoggedIn ||
                      !idToken ||
                      fetchAllShelfDetailsRunning ||
                      loadingSnapshot ||
                      loadingHondanas ||
                      !snapshotInfo
                    }
                    style={{
                      padding: "4px 10px",
                      fontSize: 11,
                      borderRadius: 999,
                      border: `1px solid ${TOKENS.border}`,
                      background:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllShelfDetailsRunning ||
                        loadingSnapshot ||
                        loadingHondanas ||
                        !snapshotInfo
                          ? TOKENS.cardBg
                          : TOKENS.glassStrong,
                      color:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllShelfDetailsRunning ||
                        loadingSnapshot ||
                        loadingHondanas ||
                        !snapshotInfo
                          ? TOKENS.textSub
                          : TOKENS.text,
                      cursor:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllShelfDetailsRunning ||
                        loadingSnapshot ||
                        loadingHondanas ||
                        !snapshotInfo
                          ? "default"
                          : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fetchAllShelfDetailsRunning ? "取得中…" : "全ての本棚の本を取得"}
                  </button>

                  {fetchAllShelfDetailsRunning && fetchAllShelfDetailsProgress && (
                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                      {fetchAllShelfDetailsProgress.done}/{fetchAllShelfDetailsProgress.total}
                    </div>
                  )}
                </>
              )}
            </div>



            {/* フィルタ & 複数選択 / グラフに追加 */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <input
                type="text"
                value={shelfFilterText}
                onChange={(e) => setShelfFilterText(e.target.value)}
                placeholder="タイトル・典拠名で絞り込み"
                style={{
                  width: "100%",
                  fontSize: 11,
                  padding: "4px 6px",
                  borderRadius: 6,
                  border: `1px solid ${TOKENS.border}`,
                  background: TOKENS.glassStrong,
                  color: TOKENS.text,
                  outline: "none",
                }}
              />

              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <button
                  onClick={handleToggleShelfMultiSelect}
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: 11,
                    borderRadius: 999,
                    border: `1px solid ${TOKENS.border}`,
                    background: shelfMultiSelectMode
                      ? "#22c55e"
                      : TOKENS.glassStrong,
                    color: shelfMultiSelectMode ? "#022c22" : TOKENS.text,
                    cursor: "pointer",
                  }}
                >
                  複数選択
                </button>
                <button
                  onClick={handleAddSelectedShelfBooksToGraph}
                  disabled={
                    !shelfMultiSelectMode || selectedShelfBookIds.length === 0
                  }
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: 11,
                    borderRadius: 999,
                    border: `1px solid ${TOKENS.border}`,
                    background:
                      !shelfMultiSelectMode || selectedShelfBookIds.length === 0
                        ? TOKENS.cardBg
                        : "#3b82f6",
                    color:
                      !shelfMultiSelectMode || selectedShelfBookIds.length === 0
                        ? TOKENS.textSub
                        : "#eff6ff",
                    cursor:
                      !shelfMultiSelectMode || selectedShelfBookIds.length === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  グラフに追加
                </button>
              </div>
            </div>

            {/* 本棚一覧 */}
            <div
              style={{
                marginTop: 6,
                flex: 1,
                overflowY: "auto",
                paddingRight: 24,
                fontSize: 11,
              }}
            >
              {loadingHondanas ? (
                <div style={{ color: TOKENS.textSub }}>本棚一覧を読み込み中…</div>
              ) : userHondanas.length === 0 ? (
                <div style={{ color: TOKENS.textSub }}>
                  本棚がまだ作成されていません。
                </div>
              ) : (
                userHondanas.map((shelf) => {
                  const id = shelf.GlobalHondanaID;
                  const isExpanded = expandedShelfIds.includes(id);
                  const booksRaw = shelfBooksMap[id] ?? [];
                  const isShelfLoading = loadingShelfIds.includes(id);
                  const isShelfEnriching = enrichingShelfIds.includes(id);
                  const shelfChecked = selectedShelfIds.includes(id);

                  const filterTerm = shelfFilterText.trim().toLowerCase();
                  const books = filterTerm
                    ? booksRaw.filter((b) => {
                        const title = (b.MainTitle ?? "").toLowerCase();
                        const resp = (b.Responsibility ?? "").toLowerCase();
                        return (
                          title.includes(filterTerm) ||
                          resp.includes(filterTerm)
                        );
                      })
                    : booksRaw;

                  return (
                    <div key={id}>
                      {/* 本棚行 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "stretch",
                          gap: 8,
                          padding: "4px 6px",
                          touchAction: "pan-y",
                          borderRadius: 6,
                          marginBottom: 4,
                          background: isExpanded
                            ? TOKENS.cardBgHover
                            : "transparent",
                          cursor: "pointer",
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        {shelfMultiSelectMode && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginRight: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={shelfChecked}
                              onChange={(e) =>
                                handleToggleShelfSelected(id, e.target.checked)
                              }
                              style={{ width: 12, height: 12 }}
                            />
                          </div>
                        )}

                        <div
                          onClick={() => handleClickShelfRow(shelf)}
                          style={{
                            width: 4,
                            borderRadius: 999,
                            background: shelf.ColorCode || "#4b5563",
                          }}
                        />
                        <div
                          onClick={() => handleClickShelfRow(shelf)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {shelf.HondanaName}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isShelfLoading
                                ? "読込中…"
                                : booksRaw.length > 0
                                ? `${booksRaw.length} 冊${isShelfEnriching ? "（書誌情報取得中…）" : ""}`
                                : ""}
                            </span>
                          </div>
                          {shelf.Description && (
                            <span
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {shelf.Description}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 展開時：棚の中の本一覧（フィルタ適用済み） */}
                      {isExpanded && (
                        <div
                          style={{
                            marginLeft: 12,
                            marginBottom: 6,
                            borderLeft:
                              `1px solid ${TOKENS.border}`,
                            paddingLeft: 6,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            minWidth: 0,
                            overflowX: "hidden",
                            touchAction: "pan-y",
                          }}
                        >
                          {isShelfLoading ? (
                            <div
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                padding: "2px 0",
                              }}
                            >
                              本棚の本を読み込み中…
                            </div>
                          ) : books.length === 0 ? (
                            <div
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                padding: "2px 0",
                              }}
                            >
                              {booksRaw.length === 0
                                ? "本がまだ登録されていません。"
                                : "絞り込み条件に一致する本がありません。"}
                            </div>
                          ) : (
                            books.map((b) => {
                              const bookChecked = selectedShelfBookIds.includes(
                                b.GlobalBookID
                              );
                              const rlBookId = b.rl_book_id != null
                                ? Number(b.rl_book_id)
                                : null;
                              const alreadyInGraph =
                                rlBookId != null && graphBookRlBookIdSet.has(rlBookId);
                              const addDisabled =
                                rlBookId == null || alreadyInGraph || loadingGraph;

                              return (
                                <div
                                  key={b.GlobalBookID}
                                  style={{
                                    padding: "2px 4px",
                                    borderRadius: 4,
                                    background:
                                      TOKENS.glassStrong,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 4,
                                    minWidth: 0,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                  }}
                                >
                                  {shelfMultiSelectMode && (
                                    <div
                                      style={{
                                        paddingTop: 2,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={bookChecked}
                                        disabled={alreadyInGraph || rlBookId == null}
                                        onChange={(e) =>
                                          handleToggleBookSelected(
                                            id,
                                            b.GlobalBookID,
                                            e.target.checked
                                          )
                                        }
                                        style={{
                                          width: 12,
                                          height: 12,
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        minWidth: 0,
                                        flex: 1,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 11,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {b.MainTitle ||
                                          (isShelfEnriching ? "(取得中…)" : "(タイトル未登録)")}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 10,
                                          color: TOKENS.textSub,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {b.Responsibility || ""}
                                      </span>
                                      {/* <span
                                        style={{
                                          fontSize: 10,
                                          color: TOKENS.textSub,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {b.rl_book_id
                                          ? `rl_book_id: ${b.rl_book_id}`
                                          : ""}
                                      </span> */}
                                    </div>
                                    {/* 1冊ずつグラフに追加する + ボタン。既に追加済みなら → でグラフ内のその本にフォーカス */}
                                    <button
                                      onClick={() =>
                                        alreadyInGraph
                                          ? rlBookId != null &&
                                            handleFocusBookInGraph(rlBookId)
                                          : handleAddOneShelfBookToGraph(b)
                                      }
                                      disabled={
                                        alreadyInGraph
                                          ? rlBookId == null
                                          : addDisabled
                                      }
                                      title={
                                        alreadyInGraph
                                          ? "グラフ内のこの本を選択して中心に表示"
                                          : "この本をグラフに追加"
                                      }
                                      style={{
                                        flexShrink: 0,
                                        width: 20,
                                        height: 20,
                                        borderRadius: 999,
                                        border: `1px solid ${TOKENS.border}`,
                                        background: alreadyInGraph
                                          ? TOKENS.buttonBg
                                          : addDisabled
                                          ? TOKENS.cardBg
                                          : "#22c55e",
                                        color: alreadyInGraph
                                          ? TOKENS.text
                                          : addDisabled
                                          ? TOKENS.textSub
                                          : "#022c22",
                                        fontSize: 11,
                                        fontWeight: alreadyInGraph ? 700 : 400,
                                        lineHeight: "18px",
                                        textAlign: "center",
                                        cursor:
                                          !alreadyInGraph && addDisabled
                                            ? "not-allowed"
                                            : "pointer",
                                        padding: 0,
                                      }}
                                    >
                                      {alreadyInGraph ? "→" : "+"}
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : leftPanelTab === "browse" && browseMode === "list" ? (
          /* リストタブ */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div>リスト: {bookLists.length} 件</div>
              <button
                onClick={handleReloadListTab}
                disabled={listTabReloading}
                title="リストデータを更新"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  padding: 0,
                  borderRadius: 999,
                  border: "none",
                  background: "transparent",
                  color: TOKENS.textSub,
                  cursor: listTabReloading ? "default" : "pointer",
                }}
              >
                <RefreshIcon
                  style={{
                    fontSize: 14,
                    animation: listTabReloading
                      ? "rl-spin 0.8s linear infinite"
                      : undefined,
                  }}
                />
              </button>

              {/* ★ 未取得 or 取得中 のときだけ表示 */}
              {(!allListDetailsFetched || fetchAllListDetailsRunning) && (
                <>
                  <button
                    onClick={handleFetchAllListDetails}
                    disabled={
                      !isLoggedIn ||
                      !idToken ||
                      fetchAllListDetailsRunning ||
                      bookLists.length === 0
                    }
                    style={{
                      padding: "4px 10px",
                      fontSize: 11,
                      borderRadius: 999,
                      border: `1px solid ${TOKENS.border}`,
                      background:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllListDetailsRunning ||
                        bookLists.length === 0
                          ? TOKENS.cardBg
                          : TOKENS.glassStrong,
                      color:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllListDetailsRunning ||
                        bookLists.length === 0
                          ? TOKENS.textSub
                          : TOKENS.text,
                      cursor:
                        !isLoggedIn ||
                        !idToken ||
                        fetchAllListDetailsRunning ||
                        bookLists.length === 0
                          ? "default"
                          : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fetchAllListDetailsRunning ? "取得中…" : "全てのリストの本を取得"}
                  </button>

                  {fetchAllListDetailsRunning && fetchAllListDetailsProgress && (
                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                      {fetchAllListDetailsProgress.done}/{fetchAllListDetailsProgress.total}
                    </div>
                  )}
                </>
              )}
            </div>

            <input
              type="text"
              value={listFilterText2}
              onChange={(e) => setListFilterText2(e.target.value)}
              placeholder="タイトル・典拠名で絞り込み"
              style={{
                width: "100%",
                fontSize: 11,
                padding: "4px 6px",
                borderRadius: 6,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.glassStrong,
                color: TOKENS.text,
                outline: "none",
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 6,
                marginTop: 2,
              }}
            >
              <button
                onClick={handleToggleListMultiSelect}
                style={{
                  flex: 1,
                  padding: "4px 6px",
                  fontSize: 11,
                  borderRadius: 999,
                  border: `1px solid ${TOKENS.border}`,
                  background: listMultiSelectMode
                    ? "#22c55e"
                    : TOKENS.glassStrong,
                  color: listMultiSelectMode ? "#022c22" : TOKENS.text,
                  cursor: "pointer",
                }}
              >
                複数選択
              </button>
              <button
                onClick={handleAddSelectedListBooksToGraph}
                disabled={
                  !listMultiSelectMode || selectedListBookIds.length === 0
                }
                style={{
                  flex: 1,
                  padding: "4px 6px",
                  fontSize: 11,
                  borderRadius: 999,
                  border: `1px solid ${TOKENS.border}`,
                  background:
                    !listMultiSelectMode || selectedListBookIds.length === 0
                      ? TOKENS.cardBg
                      : "#3b82f6",
                  color:
                    !listMultiSelectMode || selectedListBookIds.length === 0
                      ? TOKENS.textSub
                      : "#eff6ff",
                  cursor:
                    !listMultiSelectMode || selectedListBookIds.length === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                グラフに追加
              </button>
            </div>

            <div
              style={{
                marginTop: 6,
                flex: 1,
                overflowY: "auto",
                paddingRight: 24,
                fontSize: 11,
              }}
            >
              {bookLists.length === 0 ? (
                <div style={{ color: TOKENS.textSub }}>
                  リストがまだ作成されていません。
                </div>
              ) : (
                bookLists.map((list) => {
                  const id = list.user_list_id;
                  const isExpanded = expandedListIds.includes(id);
                  const itemsRaw = listItemsMap[id] ?? [];
                  const isListLoading = loadingListIds.includes(id);

                  const filterTerm = listFilterText2.trim().toLowerCase();
                  const items = filterTerm
                    ? itemsRaw.filter((it) => {
                        const title = (it.title ?? "").toLowerCase();
                        const author = (it.author ?? "").toLowerCase();
                        return (
                          title.includes(filterTerm) || author.includes(filterTerm)
                        );
                      })
                    : itemsRaw;

                  const listChecked = selectedListIds.includes(id);

                  return (
                    <div key={id}>
                      {/* リスト行 */}
                      <div
                        onClick={() => handleClickListRow(list)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "4px 6px",
                          borderRadius: 6,
                          marginBottom: 4,
                          background: isExpanded
                            ? TOKENS.cardBgHover
                            : "transparent",
                          cursor: "pointer",
                          minWidth: 0,
                        }}
                      >
                        {listMultiSelectMode && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginRight: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={listChecked}
                              onChange={(e) =>
                                handleToggleListSelected(id, e.target.checked)
                              }
                              style={{ width: 12, height: 12 }}
                            />
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {list.user_list_name}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: TOKENS.textSub,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isListLoading
                              ? "読込中…"
                              : `${list.item_count ?? itemsRaw.length} 冊`}
                          </span>
                        </div>
                      </div>

                      {/* 展開時：リストの中の本一覧 */}
                      {isExpanded && (
                        <div
                          style={{
                            marginLeft: 12,
                            marginBottom: 6,
                            borderLeft: `1px solid ${TOKENS.border}`,
                            paddingLeft: 6,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            minWidth: 0,
                            overflowX: "hidden",
                          }}
                        >
                          {isListLoading ? (
                            <div
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                padding: "2px 0",
                              }}
                            >
                              本を読み込み中…
                            </div>
                          ) : items.length === 0 ? (
                            <div
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                padding: "2px 0",
                              }}
                            >
                              {itemsRaw.length === 0
                                ? "本がまだ登録されていません。"
                                : "絞り込み条件に一致する本がありません。"}
                            </div>
                          ) : (
                            items.map((it) => {
                              const alreadyInGraph = graphBookRlBookIdSet.has(
                                it.rl_book_id
                              );
                              const addDisabled = alreadyInGraph || loadingGraph;
                              const listItemChecked = selectedListBookIds.includes(
                                `${id}::${it.rl_book_id}`
                              );
                              return (
                                <div
                                  key={it.rl_book_id}
                                  style={{
                                    padding: "2px 4px",
                                    borderRadius: 4,
                                    background: TOKENS.glassStrong,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 4,
                                    minWidth: 0,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                  }}
                                >
                                  {listMultiSelectMode && (
                                    <div style={{ paddingTop: 2 }}>
                                      <input
                                        type="checkbox"
                                        checked={listItemChecked}
                                        disabled={alreadyInGraph}
                                        onChange={(e) =>
                                          handleToggleListBookSelected(
                                            id,
                                            it.rl_book_id,
                                            e.target.checked
                                          )
                                        }
                                        style={{ width: 12, height: 12 }}
                                      />
                                    </div>
                                  )}

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        minWidth: 0,
                                        flex: 1,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 11,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {it.title ? titleWithVolume(it) : "(タイトル未登録)"}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 10,
                                          color: TOKENS.textSub,
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {it.author || ""}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() =>
                                        alreadyInGraph
                                          ? handleFocusBookInGraph(it.rl_book_id)
                                          : handleAddOneBookToGraphById(it.rl_book_id)
                                      }
                                      disabled={!alreadyInGraph && addDisabled}
                                      title={
                                        alreadyInGraph
                                          ? "グラフ内のこの本を選択して中心に表示"
                                          : "この本をグラフに追加"
                                      }
                                      style={{
                                        flexShrink: 0,
                                        width: 20,
                                        height: 20,
                                        borderRadius: 999,
                                        border: `1px solid ${TOKENS.border}`,
                                        background: alreadyInGraph
                                          ? TOKENS.buttonBg
                                          : addDisabled
                                          ? TOKENS.cardBg
                                          : "#22c55e",
                                        color: alreadyInGraph
                                          ? TOKENS.text
                                          : addDisabled
                                          ? TOKENS.textSub
                                          : "#022c22",
                                        fontSize: 11,
                                        fontWeight: alreadyInGraph ? 700 : 400,
                                        lineHeight: "18px",
                                        textAlign: "center",
                                        cursor:
                                          !alreadyInGraph && addDisabled
                                            ? "not-allowed"
                                            : "pointer",
                                        padding: 0,
                                      }}
                                    >
                                      {alreadyInGraph ? "→" : "+"}
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : leftPanelTab === "browse" && browseMode === "favorites" ? (
          /* お気に入りタブ */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div>
                お気に入り: {favoriteItems.length}
                {favoritesNextCursor ? "+" : ""} 冊
              </div>
              <button
                onClick={() => loadFavorites(true)}
                disabled={loadingFavorites}
                title="お気に入りデータを更新"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  padding: 0,
                  borderRadius: 999,
                  border: "none",
                  background: "transparent",
                  color: TOKENS.textSub,
                  cursor: loadingFavorites ? "default" : "pointer",
                }}
              >
                <RefreshIcon
                  style={{
                    fontSize: 14,
                    animation: loadingFavorites
                      ? "rl-spin 0.8s linear infinite"
                      : undefined,
                  }}
                />
              </button>
            </div>

            <div
              style={{
                marginTop: 6,
                flex: 1,
                overflowY: "auto",
                paddingRight: 24,
                fontSize: 11,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {loadingFavorites && favoriteItems.length === 0 ? (
                <div style={{ color: TOKENS.textSub }}>
                  お気に入りを読み込み中…
                </div>
              ) : favoriteItems.length === 0 ? (
                <div style={{ color: TOKENS.textSub }}>
                  お気に入りの本がまだありません。
                </div>
              ) : (
                <>
                  {favoriteItems.map((fav) => {
                    const alreadyInGraph = graphBookRlBookIdSet.has(
                      fav.rl_book_id
                    );
                    const addDisabled = alreadyInGraph || loadingGraph;
                    return (
                      <div
                        key={fav.rl_book_id}
                        style={{
                          padding: "2px 4px",
                          borderRadius: 4,
                          background: TOKENS.glassStrong,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 4,
                          minWidth: 0,
                          flexShrink: 0,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {fav.title ? titleWithVolume(fav) : "(タイトル未登録)"}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {fav.author || ""}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              alreadyInGraph
                                ? handleFocusBookInGraph(fav.rl_book_id)
                                : handleAddOneBookToGraphById(fav.rl_book_id)
                            }
                            disabled={!alreadyInGraph && addDisabled}
                            title={
                              alreadyInGraph
                                ? "グラフ内のこの本を選択して中心に表示"
                                : "この本をグラフに追加"
                            }
                            style={{
                              flexShrink: 0,
                              width: 20,
                              height: 20,
                              borderRadius: 999,
                              border: `1px solid ${TOKENS.border}`,
                              background: alreadyInGraph
                                ? TOKENS.buttonBg
                                : addDisabled
                                ? TOKENS.cardBg
                                : "#22c55e",
                              color: alreadyInGraph
                                ? TOKENS.text
                                : addDisabled
                                ? TOKENS.textSub
                                : "#022c22",
                              fontSize: 11,
                              fontWeight: alreadyInGraph ? 700 : 400,
                              lineHeight: "18px",
                              textAlign: "center",
                              cursor:
                                !alreadyInGraph && addDisabled
                                  ? "not-allowed"
                                  : "pointer",
                              padding: 0,
                            }}
                          >
                            {alreadyInGraph ? "→" : "+"}
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {favoritesNextCursor && (
                    <button
                      onClick={() => loadFavorites(false)}
                      disabled={loadingFavorites}
                      style={{
                        marginTop: 6,
                        padding: "6px 8px",
                        fontSize: 11,
                        borderRadius: 999,
                        border: `1px solid ${TOKENS.border}`,
                        background: TOKENS.glassStrong,
                        color: TOKENS.text,
                        cursor: loadingFavorites ? "default" : "pointer",
                      }}
                    >
                      {loadingFavorites ? "読み込み中…" : "さらに読み込む"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          /* 表示中の典拠と本タブ */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              paddingRight: 24,
            }}
          >
            {/* チェック & 絞り込み */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={groupByAuthority}
                  onChange={(e) =>
                    setGroupByAuthority(e.target.checked)
                  }
                  style={{ width: 12, height: 12 }}
                />
                <span>典拠でまとめる</span>
              </label>

              <input
                type="text"
                value={listFilterText}
                onChange={(e) => setListFilterText(e.target.value)}
                placeholder="タイトル・典拠名で絞り込み"
                style={{
                  width: "100%",
                  fontSize: 11,
                  padding: "4px 6px",
                  borderRadius: 6,
                  border: `1px solid ${TOKENS.border}`,
                  background: TOKENS.glassStrong,
                  color: TOKENS.text,
                  outline: "none",
                }}
              />
            </div>

            {/* 一覧 */}
            <div
              style={{
                marginTop: 6,
                flex: 1,
                overflowY: "auto",
                paddingRight: 24,
              }}
            >
              {groupByAuthority ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  {authorNodes.map((author) => {
                    const allBooks = authorToBooks.get(author.id) ?? [];
                    const filterTerm = listFilterText.trim();

                    const booksMatching = filterTerm
                      ? allBooks.filter((b) =>
                          matchesFilter(b, filterTerm)
                        )
                      : allBooks;

                    const authorMatches = filterTerm
                      ? matchesFilter(author, filterTerm)
                      : true;

                    if (filterTerm) {
                      if (
                        !authorMatches &&
                        booksMatching.length === 0
                      ) {
                        return null;
                      }
                    }

                    const isSelected =
                      selectedNode &&
                      selectedNode.id === author.id &&
                      selectedNode.type === "author";

                    const isExpanded = filterTerm
                      ? booksMatching.length > 0 || authorMatches
                      : expandedAuthorRowIds.includes(author.id);

                    const booksToShow = filterTerm
                      ? booksMatching
                      : allBooks;

                    const handleClickAuthorRow = () => {
                      handleNodeClick(author);

                      if (!filterTerm) {
                        setExpandedAuthorRowIds((prev) =>
                          prev.includes(author.id)
                            ? prev.filter(
                                (id) => id !== author.id
                              )
                            : [...prev, author.id]
                        );
                      }
                    };

                    return (
                      <div key={author.id}>
                        {/* 著者行 */}
                        <div
                          onClick={handleClickAuthorRow}
                          style={{
                            padding: "4px 6px",
                            borderRadius: 6,
                            cursor: "pointer",
                            background: isSelected
                              ? TOKENS.border
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                              "space-between",
                            gap: 4,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                textOverflow:
                                  "ellipsis",
                                overflow: "hidden",
                                maxWidth: 190,
                              }}
                            >
                              {author.name ??
                                author.label ??
                                author.id}
                            </span>
                            {/* <span
                              style={{
                                fontSize: 10,
                                color: TOKENS.textSub,
                              }}
                            >
                              {author.tenkyoId
                                ? `TENKYO_ID: ${author.tenkyoId}`
                                : author.id}
                            </span> */}
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              color: TOKENS.textSub,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {allBooks.length} 冊
                          </span>
                        </div>

                        {/* 本のリスト */}
                        {isExpanded &&
                          booksToShow.length > 0 && (
                            <div
                              style={{
                                marginLeft: 10,
                                marginTop: 2,
                                marginBottom: 4,
                                borderLeft:
                                  `1px solid ${TOKENS.border}`,
                                paddingLeft: 6,
                                display: "flex",
                                flexDirection:
                                  "column",
                                gap: 2,
                              }}
                            >
                              {booksToShow.map(
                                (book) => {
                                  const isBookSelected =
                                    selectedNode &&
                                    selectedNode.id ===
                                      book.id &&
                                    selectedNode.type ===
                                      "book";
                                  return (
                                    <div
                                      key={book.id}
                                      onClick={() =>
                                        handleNodeClick(
                                          book
                                        )
                                      }
                                      style={{
                                        padding:
                                          "2px 4px",
                                        borderRadius: 4,
                                        cursor:
                                          "pointer",
                                        background:
                                          isBookSelected
                                            ? "rgba(59,130,246,0.4)"
                                            : "transparent",
                                        display:
                                          "flex",
                                        flexDirection:
                                          "column",
                                        gap: 1,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 11,
                                          whiteSpace:
                                            "nowrap",
                                          textOverflow:
                                            "ellipsis",
                                          overflow:
                                            "hidden",
                                        }}
                                      >
                                        {book.title ??
                                          book.label ??
                                          book.id}
                                      </span>
                                      {/* <span
                                        style={{
                                          fontSize: 10,
                                          color:
                                            TOKENS.textSub,
                                          whiteSpace:
                                            "nowrap",
                                          textOverflow:
                                            "ellipsis",
                                          overflow:
                                            "hidden",
                                        }}
                                      >
                                        {book.rlBookId
                                          ? `rl_book_id: ${book.rlBookId}`
                                          : book.id}
                                      </span> */}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* 典拠でまとめない：フラット表示 */
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  {(() => {
                    const filterTerm =
                      listFilterText.trim();

                    const authorsWithMatchedBooks =
                      new Set<string>();
                    if (filterTerm) {
                      for (const [
                        bookId,
                        authors,
                      ] of bookToAuthors) {
                        const bookNode =
                          graphData.nodes.find(
                            (n) => n.id === bookId
                          );
                        if (
                          bookNode &&
                          matchesFilter(
                            bookNode,
                            filterTerm
                          )
                        ) {
                          for (const a of authors) {
                            authorsWithMatchedBooks.add(
                              a.id
                            );
                          }
                        }
                      }
                    }

                    const items: GraphNode[] = [];

                    for (const a of authorNodes) {
                      if (!filterTerm) {
                        items.push(a);
                      } else {
                        const authorMatches =
                          matchesFilter(a, filterTerm);
                        if (
                          authorMatches ||
                          authorsWithMatchedBooks.has(
                            a.id
                          )
                        ) {
                          items.push(a);
                        }
                      }
                    }
                    for (const b of bookNodes) {
                      if (
                        !filterTerm ||
                        matchesFilter(b, filterTerm)
                      ) {
                        items.push(b);
                      }
                    }

                    return items.map((node) => {
                      const isSelected =
                        selectedNode &&
                        selectedNode.id === node.id;
                      const isBook =
                        node.type === "book";

                      return (
                        <div
                          key={node.id}
                          onClick={() =>
                            handleNodeClick(node)
                          }
                          style={{
                            padding: "3px 6px",
                            borderRadius: 6,
                            cursor: "pointer",
                            background: isSelected
                              ? isBook
                                ? "rgba(59,130,246,0.5)"
                                : TOKENS.border
                              : "transparent",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                padding:
                                  "1px 4px",
                                borderRadius: 999,
                                border:
                                  `1px solid ${TOKENS.border}`,
                                color: TOKENS.textSub,
                              }}
                            >
                              {isBook ? "本" : "著"}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                whiteSpace:
                                  "nowrap",
                                textOverflow:
                                  "ellipsis",
                                overflow:
                                  "hidden",
                              }}
                            >
                              {isBook
                                ? node.title ??
                                  node.label ??
                                  node.id
                                : node.name ??
                                  node.label ??
                                  node.id}
                            </span>
                          </div>
                          {/* <span
                            style={{
                              fontSize: 10,
                              color:
                                TOKENS.textSub,
                              whiteSpace:
                                "nowrap",
                              textOverflow:
                                "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {isBook
                              ? node.rlBookId
                                ? `rl_book_id: ${node.rlBookId}`
                                : node.id
                              : node.tenkyoId
                              ? `TENKYO_ID: ${node.tenkyoId}`
                              : node.id}
                          </span> */}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      ) : (
        <button
          onClick={() => setIsLeftPanelCollapsed(false)}
          style={{
            position: "absolute",
            top: 88,
            left: 12,
            zIndex: 23,
            padding: "6px 10px",
            fontSize: 11,
            borderRadius: 999,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glassStrong,
            color: TOKENS.text,
            cursor: "pointer",
            boxShadow: "0 10px 20px rgba(0,0,0,0.35)",
          }}
          title="左パネルを開く"
        >
          ▶
        </button>
      )}

      
      {/* グラフをクリア ボタン */}
      <button
        onClick={handleClickClearGraph}
        style={{
            position: "fixed", // ← absolute から fixed へ
            bottom: 12,
            right: 12 + (selectedNode ? 320 : 0),
            zIndex: 2000,      // ← ちょっと強めに
            padding: "6px 14px",
            fontSize: 11,
            borderRadius: 999,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glassStrong,
            color: TOKENS.text,
            cursor: "pointer",
        }}
        >
        グラフをクリア
      </button>

      {/* グラフクリア確認モーダル */}
      {showClearConfirm && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: TOKENS.glass,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2200, // ボタンより上
          }}
        >
          <div
            style={{
              minWidth: 260,
              maxWidth: "80vw",
              padding: 16,
              borderRadius: 12,
              background: TOKENS.glassStrong,
              border: `1px solid ${TOKENS.border}`,
              boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
              color: TOKENS.text,
              fontSize: 13,
            }}
          >
            <div style={{ marginBottom: 12 }}>
              グラフをクリアしますか？
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 4,
              }}
            >
              <button
                onClick={handleCancelClearGraph}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  borderRadius: 999,
                  border: `1px solid ${TOKENS.border}`,
                  background: TOKENS.glassStrong,
                  color: TOKENS.text,
                  cursor: "pointer",
                }}
              >
                いいえ
              </button>
              <button
                onClick={handleConfirmClearGraph}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  borderRadius: 999,
                  border: "1px solid #7f1d1d",
                  background: "#b91c1c",
                  color: "#fef2f2",
                  cursor: "pointer",
                }}
              >
                はい
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 右側スライドパネル */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          height: "100%",
          width: 320,
          maxWidth: "80vw",
          background: TOKENS.glassStrong,
          borderLeft: `1px solid ${TOKENS.border}`,
          color: TOKENS.text,
          padding: 16,
          boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
          transform: selectedNode ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.25s ease-out",
          zIndex:40,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          pointerEvents: selectedNode ? "auto" : "none",
        }}
      >
        {selectedNode && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: TOKENS.textSub,
                    marginBottom: 4,
                  }}
                >
                  {selectedNode.type === "book" ? "Book" : "Author"}
                </div>
                <h2
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    marginRight: 8,
                  }}
                >
                  {selectedNode.type === "book"
                    ? (selectedNode.title
                        ? titleWithVolume(selectedNode)
                        : selectedNode.label ?? selectedNode.id)
                    : selectedNode.name ??
                      selectedNode.label ??
                      selectedNode.id}
                </h2>
              </div>
              <IconButton
                onClick={handleClosePanel}
                sx={{
                  color: TOKENS.textSub,
                  borderRadius: "8px",
                  p: 1,
                  "&:hover": { background: TOKENS.buttonBgHover },
                }}
              >
                <CloseIcon fontSize="medium" />
              </IconButton>
            </div>

            {/* ヘッダー(タイトル・閉じるボタン)より下、書影を含めた本文全体をスクロール
                可能にする。スマホ等の縦幅が狭い画面で、下側の情報が見えなくなる
                不具合への対応（2026-08-29） */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {selectedNode.type === "book" && (
              <>
                {(() => {
                  const cover =
                    selectedNode.coverUrl ??
                    (selectedNode.rlBookId != null || selectedNode.title
                      ? generateCoverUrlSmart(selectedNode.rlBookId, selectedNode.title, selectedNode.isbn)
                      : undefined);

                  const rlBookId = selectedNode.rlBookId ?? Number(selectedNode.id);

                  const bookUrl = rlBookId != null && !isNaN(rlBookId)
                    ? `/book/${rlBookId}`
                    : undefined;

                  if (!cover) return null;
                  return (
                    <>
                      {bookUrl && (
                        <div style={{ marginBottom: 8 }}>
                          <Button
                            component="a"
                            href={bookUrl}
                            target="_blank"
                            rel="noreferrer"
                            fullWidth
                            size="large"
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              py: 1.5,
                              borderRadius: "999px",
                              color: TOKENS.bg,
                              background: TOKENS.text,
                              "&:hover": { background: TOKENS.textSub },
                            }}
                          >
                            本の詳細を見る
                          </Button>
                        </div>
                      )}

                      {bookUrl ? (
                        <a
                          href={bookUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: "block" }}
                        >
                          <img
                            src={cover}
                            alt={selectedNode.title ?? selectedNode.label ?? ""}
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: 220,
                              borderRadius: 4,
                              background: TOKENS.cardBg,
                              objectFit: "contain",
                            }}
                          />
                        </a>
                      ) : (
                        <img
                          src={cover}
                          alt={selectedNode.title ?? selectedNode.label ?? ""}
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: 220,
                            borderRadius: 4,
                            background: TOKENS.cardBg,
                            objectFit: "contain",
                          }}
                        />
                      )}

                      {(() => {
                        const canUseList = isLoggedIn && !!idToken;
                        const isFavorite =
                          rlBookId != null &&
                          !isNaN(rlBookId) &&
                          favoritesState.favoriteBooks.some(
                            (b) => b.rl_book_id === rlBookId
                          );
                        return (
                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <button
                              onClick={canUseList ? handleListMenuOpen : undefined}
                              disabled={!canUseList}
                              style={{
                                flex: 1,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 300,
                                borderRadius: 999,
                                border: `1px solid ${TOKENS.border}`,
                                background: TOKENS.cardBg,
                                color: canUseList ? TOKENS.text : TOKENS.textSub,
                                cursor: canUseList ? "pointer" : "not-allowed",
                              }}
                              title={
                                canUseList
                                  ? "リストへ追加"
                                  : "ログインするとリストへ追加できます"
                              }
                            >
                              リストへ追加
                            </button>

                            <button
                              onClick={
                                canUseList && rlBookId != null && !isNaN(rlBookId)
                                  ? () => handleFavoriteToggle(rlBookId)
                                  : undefined
                              }
                              disabled={!canUseList}
                              title={
                                canUseList
                                  ? isFavorite
                                    ? "お気に入りから外す"
                                    : "お気に入りに追加"
                                  : "ログインするとお気に入りに追加できます"
                              }
                              style={{
                                flexShrink: 0,
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 999,
                                border: `1px solid ${TOKENS.border}`,
                                background: TOKENS.cardBg,
                                color: isFavorite
                                  ? "rgba(250,204,21,0.95)"
                                  : TOKENS.textSub,
                                cursor: canUseList ? "pointer" : "not-allowed",
                                padding: 0,
                              }}
                            >
                              {isFavorite ? (
                                <StarIcon style={{ fontSize: 18 }} />
                              ) : (
                                <StarBorderIcon style={{ fontSize: 18 }} />
                              )}
                            </button>
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
                {(() => {
                  const raw = (selectedNode.raw ?? {}) as any;

                  const publisher: string | undefined =
                    selectedNode.publisher ?? raw.publisher;

                  // 著者名はリンク情報から引く（なければ API の author / Responsibility を fallback）
                  const authorsFromLinks =
                    bookToAuthors.get(selectedNode.id) ?? [];
                  const authorNames = authorsFromLinks
                    .map((a) => a.name ?? a.label ?? a.id)
                    .filter(Boolean);

                  const responsibility: string | undefined =
                    raw.author ?? raw.Responsibility ?? raw.responsibility;

                  const authorLine =
                    authorNames.length > 0
                      ? authorNames.join(" / ")
                      : responsibility;

                  const pubYear = selectedNode.pubYear ?? raw.pubYear ?? raw.pub_year;

                  return (
                    <>
                      <div style={{ fontSize: 12, color: TOKENS.textSub }}>
                        {authorLine && <div>著者: {authorLine}</div>}
                        {publisher && <div>出版社: {publisher}</div>}
                        {pubYear != null && <div>出版日: {pubYear}</div>}
                        {selectedNode.isbn && (
                          <div>ISBN: {selectedNode.isbn}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 8 }}>
                        本棚: {selectedNode.isInUserShelf ? "あり" : "なし"}
                      </div>
                    </>
                  );
                })()}
                {(() => {
                  const raw = (selectedNode.raw ?? {}) as any;
                  const mainTitle =
                    selectedNode.title ??
                    raw.MainTitle ??
                    raw.mainTitle ??
                    selectedNode.label ??
                    "";

                  // NDL リンクは raw データから ndl_bib_id を取得
                  const ndlBibId = raw.ndl_bib_id ?? raw.KANRI_ID ?? raw.kanriId;
                  const ndlUrl = ndlBibId
                    ? `https://ndlsearch.ndl.go.jp/books/R100000002-I${ndlBibId}`
                    : undefined;

                  const looksLikeButton: React.CSSProperties = {
                    display: "inline-block",
                    padding: "4px 8px",
                    fontSize: 11,
                    fontWeight: 300,
                    borderRadius: 999,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    color: TOKENS.text,
                    textDecoration: "none",
                    cursor: "pointer",
                  };

                  const isbn = selectedNode.isbn;
                  const amazonUrl = isbn ? createAmazonLink(isbn, mainTitle) : undefined;
                  const calilUrl = isbn ? createCalilLink(isbn) : undefined;

                  return (
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "nowrap",
                        marginTop: 8,
                      }}
                    >
                      {/* ISBNがある時だけ */}
                      {isbn && amazonUrl && (
                        <a href={amazonUrl} target="_blank" rel="noreferrer" style={looksLikeButton}>
                          Amazon
                        </a>
                      )}

                      {/* NDLは全ての本で */}
                      {ndlUrl && (
                        <a href={ndlUrl} target="_blank" rel="noreferrer" style={looksLikeButton}>
                          国会図書館
                        </a>
                      )}

                      {/* ISBNがある時だけ */}
                      {isbn && calilUrl && (
                        <a href={calilUrl} target="_blank" rel="noreferrer" style={looksLikeButton}>
                          カーリル
                        </a>
                      )}
                    </div>
                  );
                })()}

                {panelBookAuthors.length > 0 &&
                  (() => {
                    const allCoAuthorsShown = panelBookAuthors.every(
                      (a) =>
                        !!a.tenkyoId &&
                        nodeMapRef.current.has(`author:${a.tenkyoId}`)
                    );
                    return (
                      <button
                        onClick={handleShowAllCoAuthorsBooks}
                        disabled={loadingGraph || allCoAuthorsShown}
                        style={{
                          marginTop: 10,
                          width: "100%",
                          padding: "6px 10px",
                          fontSize: 12,
                          borderRadius: 999,
                          border: `1px solid ${TOKENS.border}`,
                          background: allCoAuthorsShown
                            ? TOKENS.cardBgHover
                            : "#22c55e",
                          cursor: allCoAuthorsShown ? "not-allowed" : "pointer",
                          color: allCoAuthorsShown ? TOKENS.textSub : "#022c22",
                          opacity: allCoAuthorsShown ? 0.6 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                      >
                        {loadingGraph && !allCoAuthorsShown && (
                          <RefreshIcon
                            style={{ fontSize: 14, animation: "rl-spin 0.8s linear infinite" }}
                          />
                        )}
                        {allCoAuthorsShown
                          ? "全ての共著者の本を表示中"
                          : loadingGraph
                          ? "表示中…"
                          : "全ての共著者の本を表示"}
                      </button>
                    );
                  })()}

                {panelBookAuthorsLoading && panelBookAuthors.length === 0 ? (
                  <div style={{ fontSize: 11, color: TOKENS.textSub, marginTop: 10 }}>
                    著者を読み込み中…
                  </div>
                ) : (
                  panelBookAuthors.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginTop: 10,
                      }}
                    >
                      {panelBookAuthors.map((author) => {
                        const inGraph =
                          !!author.tenkyoId &&
                          nodeMapRef.current.has(`author:${author.tenkyoId}`);
                        return (
                          <button
                            key={author.id}
                            onClick={() =>
                              author.tenkyoId &&
                              handleAddAuthorToGraphById(author.tenkyoId)
                            }
                            style={{
                              padding: "3px 9px",
                              fontSize: 10.5,
                              borderRadius: 999,
                              border: `1px solid ${
                                !inGraph ? TOKENS.accent : TOKENS.border
                              }`,
                              background: !inGraph
                                ? TOKENS.cardBgHover
                                : TOKENS.cardBg,
                              color: !inGraph ? TOKENS.text : TOKENS.textSub,
                              cursor: "pointer",
                            }}
                          >
                            {author.name ?? author.label ?? author.id}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
              </>
            )}

            {selectedNode.type === "author" && (
              <>
                {(() => {
                  const tenkyoId =
                    selectedNode.tenkyoId ??
                    (selectedNode.id.startsWith("author:")
                      ? selectedNode.id.split(":")[1]
                      : selectedNode.id);
                  if (!tenkyoId) return null;
                  const authorUrl = `https://readlinker.com/author/${tenkyoId}`;
                  return (
                    <div style={{ marginBottom: 8 }}>
                      <Button
                        component="a"
                        href={authorUrl}
                        target="_blank"
                        rel="noreferrer"
                        fullWidth
                        size="large"
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          py: 1.5,
                          borderRadius: "999px",
                          color: TOKENS.bg,
                          background: TOKENS.text,
                          "&:hover": { background: TOKENS.textSub },
                        }}
                      >
                        著者の詳細を見る
                      </Button>
                    </div>
                  );
                })()}

                {(() => {
                  const tenkyoId =
                    selectedNode.tenkyoId ??
                    (selectedNode.id.startsWith("author:")
                      ? selectedNode.id.split(":")[1]
                      : selectedNode.id);

                  if (!tenkyoId) return null;

                  const alreadyExpandedAuthor =
                    expandedAuthorTenkyoIds.includes(tenkyoId);

                  const progressForThisAuthor =
                    authorExpandProgress &&
                    authorExpandProgress.tenkyoId === tenkyoId
                      ? authorExpandProgress
                      : null;

                  const disabled =
                    alreadyExpandedAuthor || !!progressForThisAuthor;

                  let label = "著者の全ての本を表示";
                  if (alreadyExpandedAuthor) {
                    label = "著者の全ての本を表示済み";
                  } else if (progressForThisAuthor) {
                    label = `表示中… ${progressForThisAuthor.done}/${progressForThisAuthor.total}`;
                  }

                  return (
                    <>
                      <button
                        onClick={handleExpandAuthorAllBooks}
                        disabled={disabled}
                        style={{
                          marginTop: 8,
                          width: "100%",
                          padding: "6px 10px",
                          fontSize: 12,
                          borderRadius: 999,
                          border: `1px solid ${TOKENS.border}`,
                          background:
                            alreadyExpandedAuthor || progressForThisAuthor
                              ? TOKENS.cardBgHover
                              : "#22c55e",
                          cursor: disabled ? "not-allowed" : "pointer",
                          color: disabled ? TOKENS.textSub : "#022c22",
                          opacity: alreadyExpandedAuthor ? 0.6 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                      >
                        {progressForThisAuthor && (
                          <RefreshIcon
                            style={{ fontSize: 14, animation: "rl-spin 0.8s linear infinite" }}
                          />
                        )}
                        {label}
                      </button>
                      {progressForThisAuthor && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 11,
                            color: TOKENS.textSub,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span>
                            {progressForThisAuthor.done}/
                            {progressForThisAuthor.total} 冊 表示完了
                          </span>

                          <button
                            onClick={() => handleCancelAuthorExpand(tenkyoId)}
                            style={{
                              fontSize: 10,
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: `1px solid ${TOKENS.error}`,
                              background: "transparent",
                              color: TOKENS.error,
                              cursor: "pointer",
                            }}
                          >
                            止める
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}

                <div
                  style={{
                    fontSize: 12,
                    marginTop: 8,
                    color: TOKENS.text,
                    lineHeight: 1.6,
                  }}
                >
                </div>
              </>
            )}
            </div>
          </>
        )}
      </div>


      {/* リストに追加メニュー */}
      <Menu
        anchorEl={listMenuAnchorEl}
        open={Boolean(listMenuAnchorEl)}
        onClose={handleListMenuClose}
        PaperProps={{
          sx: {
            bgcolor: TOKENS.glassStrong,
            color: TOKENS.text,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 2,
          },
        }}
      >
        {bookLists.map((list) => {
          const isInList = inListIds.has(list.user_list_id);
          return (
            <MenuItem
              key={list.user_list_id}
              onClick={() => handleToggleListItem(list.user_list_id)}
              sx={{ fontSize: 12, display: "flex", alignItems: "center", gap: 1 }}
            >
              {isInList ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e", flexShrink: 0 }} />
              ) : (
                <Box sx={{ width: 16, flexShrink: 0 }} />
              )}
              {list.user_list_name}
            </MenuItem>
          );
        })}
        <MenuItem onClick={handleCreateNewList} sx={{ fontSize: 12 }}>
          ＋新しいリストを作成
        </MenuItem>
      </Menu>

      {/* 新規リスト作成ダイアログ */}
      <Dialog
        open={openCreateListDialog}
        onClose={handleCloseCreateListDialog}
        PaperProps={{
          sx: {
            bgcolor: TOKENS.glassStrong,
            color: TOKENS.text,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 3,
            minWidth: 360,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 300 }}>新しいリストを作成</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="リスト名"
            type="text"
            fullWidth
            variant="outlined"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            InputLabelProps={{ sx: { color: TOKENS.text } }}
            InputProps={{
              sx: {
                color: TOKENS.text,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.accent,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.accent,
                },
              },
            }}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel
              id="open-level-label"
              sx={{ color: TOKENS.text }}
            >
              公開範囲
            </InputLabel>
            <Select
              labelId="open-level-label"
              id="open-level"
              value={newListOpenLevel}
              label="公開範囲"
              onChange={(e) => setNewListOpenLevel(e.target.value as ListOpenLevel)}
              sx={{
                color: TOKENS.text,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.accent,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: TOKENS.accent,
                },
                "& .MuiSvgIcon-root": { color: TOKENS.text },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: TOKENS.glassStrong,
                    color: TOKENS.text,
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: 2,
                  },
                },
              }}
            >
              <MenuItem value="public">公開</MenuItem>
              <MenuItem value="url_share">リンク共有</MenuItem>
              <MenuItem value="friends">友達のみ</MenuItem>
              <MenuItem value="private">非公開</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCreateListDialog}
            disabled={isCreatingList}
            sx={{ fontWeight: 300, color: TOKENS.text }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleCreateList}
            disabled={isCreatingList || !newListName.trim()}
            sx={{
              fontWeight: 300,
              color: "#022c22",
              bgcolor: "#22c55e",
              "&:hover": { bgcolor: "#16a34a" },
              "&.Mui-disabled": {
                bgcolor: TOKENS.cardBgHover,
                color: TOKENS.textSub,
              },
            }}
          >
            {isCreatingList ? "作成中..." : "作成"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 未ログイン初回訪問者向けオンボーディングモーダル（OKボタンのみで閉じる、外側クリック・Escでは閉じない） */}
      <Dialog
        open={showOnboardingModal}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            bgcolor: TOKENS.glassStrong,
            color: TOKENS.text,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 3,
            minWidth: 320,
            maxWidth: 420,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
            この画面では、本から本へとつながる、典拠情報のネットワークを見ることができます。まずは検索欄でキーワードを入れて、表示したい本や著者を選んでください。そこからどんどん本のつながりを伸ばしていきましょう。
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
          <Button
            onClick={handleCloseOnboardingModal}
            variant="contained"
            sx={{
              fontWeight: 300,
              color: "#022c22",
              bgcolor: "#22c55e",
              "&:hover": { bgcolor: "#16a34a" },
              px: 4,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />


      {/* フッター（画面下に固定：表示確認用） */}
    <div
        style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 15,
            // 以前は999999と極端に大きい値だったため、右サイドパネル(zIndex:40)等
            // あらゆる要素より手前に出てしまっていた。常に最背面でよいので
            // 最小の値にする（2026-08-29）
            zIndex: 1,
            textAlign: "center",
            fontSize: 9,
            color: TOKENS.textSub,
            pointerEvents: "none",
            userSelect: "none",
        }}
        >
        © 2023-2026 ADA LLC.
    </div>

    </main>
    </Box>
  );
};

export default GraphPage;